/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('ai-portrait-studio')) return;

  var html = document.documentElement;

  var POLL_MS = 3000;
  var TICK_MS = 1000;
  var MISC_TIMEOUT = 8000;
  var HEALTH_TIMEOUT = 3000;

  var GENERATE_TIMEOUT = 45000;
  var POLL_DEADLINE_MS = 10 * 60 * 1000;
  var MAX_POLL_FAILS = 4;
  var MAX_EDGE = 1600;
  var JPEG_QUALITY = 0.85;
  var DEFAULT_ETA_S = 60;
  var QUIZ_ADVANCE_MS = 2400;

  var MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
  var MAX_UPLOAD_PIXELS = 250 * 1000 * 1000;

  var PROMPT_MIN = 12;
  var PROMPT_MIN_CJK = 4;
  var PROMPT_MAX = 400;

  var PROMPT_STRIP = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;

  var PROMPT_BLOCK = /[<>{}\[\]`\\|]|https?:|www\.|data:/i;
  var PROMPT_REPEAT = /(.)\1{9,}/u;
  var PROMPT_CJK = /[\u3040-\u30FF\u3400-\u9FFF\uAC00-\uD7AF]/g;

  var ASPECTS = { landscape: true, portrait: true, square: true };
  var AUTO_STYLE_ID = 'auto';

  var STAGE_FLOORS = { queued: 4, handoff: 14, checking: 22, painting: 32, finishing: 88 };

  var STYLE_GROUP_IDS = {
    pets: ['animal', 'dogs', 'cats', 'horses', 'birds', 'wolf', 'bears', 'sealife'],
    people: ['portrait', 'face', 'couple', 'women', 'dancing', 'sports'],
    places: ['landscape', 'mountain', 'beach', 'ocean', 'water', 'sky', 'night', 'architecture', 'urban', 'landmark', 'interior', 'sailing', 'industrial'],
    seasons: ['spring', 'summer', 'autumn', 'winter'],
    movements: ['impressionism', 'cubism', 'abstract', 'geometric', 'minimalist', 'modern', 'vintage'],
    vibes: ['fantasy', 'romance', 'boho', 'dark-academia', 'motivational', 'fortune', 'festival', 'patriotic', 'motion', 'music', 'wine']
  };
  var STYLE_GROUP_LOOKUP = (function () {
    var map = {};
    Object.keys(STYLE_GROUP_IDS).forEach(function (group) {
      STYLE_GROUP_IDS[group].forEach(function (id) { map[id] = group; });
    });
    return map;
  })();

  var QUIZ_EXCLUDED_HANDLES = { 'new-releases-archive': true, frontpage: true };

  function motionOn() {
    return !!window.gsap && html.classList.contains('motion');
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  var SESSION_ARTWORK_MAX = 12;

  function interp(str, vars) {
    return String(str == null ? '' : str).replace(/\[(\w+)\]/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : m;
    });
  }

  function groupForStyle(id) {
    var norm = String(id == null ? '' : id).toLowerCase().replace(/[\s_]+/g, '-');
    return STYLE_GROUP_LOOKUP[norm] || 'more';
  }

  function titleCase(str) {
    var s = String(str == null ? '' : str).trim();
    if (!s || /[A-Z]/.test(s)) return s;
    return s.replace(/(^|[\s-])([a-z])/g, function (m, pre, ch) {
      return pre + ch.toUpperCase();
    });
  }

  function formatClock(seconds) {
    var s = Math.max(0, Math.floor(seconds || 0));
    var r = s % 60;
    return Math.floor(s / 60) + ':' + (r < 10 ? '0' : '') + r;
  }

  function sizedImage(src, width) {
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  function fetchWithTimeout(url, opts) {
    opts = opts || {};
    var timeout = opts.timeout || MISC_TIMEOUT;
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, timeout);
    var init = {
      method: opts.method || 'GET',
      headers: opts.headers,
      body: opts.body,
      credentials: 'omit',
      signal: controller.signal
    };
    return fetch(url, init).finally(function () { clearTimeout(timer); });
  }

  function fetchJSON(url, opts) {
    return fetchWithTimeout(url, opts).then(function (res) {
      if (!res.ok) {
        var err = new Error('HTTP ' + res.status);
        err.status = res.status;
        err.response = res;
        throw err;
      }
      return res.json();
    });
  }

  function safeJson(res) {
    return res.json().catch(function () { return null; });
  }

  function isLateJob(job) {
    if (!job) return false;
    if (job.lateDelivery === true || job.late_delivery === true) return true;
    if (job.status === 'late') return true;
    return /my ai artworks/i.test(typeof job.error === 'string' ? job.error : '');
  }

  function normalizePrompt(raw) {
    var s = String(raw == null ? '' : raw);
    if (typeof s.normalize === 'function') s = s.normalize('NFC');
    return s.replace(PROMPT_STRIP, '').replace(/\s+/g, ' ').trim();
  }

  function validatePrompt(raw) {
    var p = normalizePrompt(raw);
    var cjk = (p.match(PROMPT_CJK) || []).length;
    if (!p) return { ok: false, code: 'empty', text: p };
    if (p.length > PROMPT_MAX) return { ok: false, code: 'long', text: p };
    if (p.length < PROMPT_MIN && cjk < PROMPT_MIN_CJK) return { ok: false, code: 'short', text: p };
    if (PROMPT_BLOCK.test(p) || PROMPT_REPEAT.test(p)) return { ok: false, code: 'chars', text: p };
    return { ok: true, text: p };
  }

  function excerpt(str, max) {
    var s = normalizePrompt(str);
    if (s.length <= max) return s;
    return s.slice(0, Math.max(1, max - 1)).replace(/\s+$/, '') + '…';
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var CURATOR_PARAMS = ['cur', 'cexp', 'csig', 'w', 'h', 'd'];
  var CURATOR_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var CURATOR_DEPTHS = { '0.75': '3/4 Inch', '1.5': '1 1/2 Inch' };
  var CURATOR_IMAGE_TYPE = /^image\/(png|jpeg|webp)$/i;
  var CURATOR_FETCH_TIMEOUT = 30000;

  function curatorInches(raw) {
    var s = String(raw == null ? '' : raw).trim();
    if (!/^\d{1,2}(?:\.\d{1,3})?$/.test(s)) return NaN;
    return Math.round(parseFloat(s) * 10) / 10;
  }

  function parseCuratorLink(params) {
    var id = String(params.get('cur') || '');
    var exp = String(params.get('cexp') || '');
    var sig = String(params.get('csig') || '');
    if (!CURATOR_UUID.test(id) || !/^\d{1,12}$/.test(exp) || !/^[0-9a-f]{16,128}$/i.test(sig)) return null;
    var w = curatorInches(params.get('w'));
    var h = curatorInches(params.get('h'));
    if (!isFinite(w) || !isFinite(h)) return null;
    var shortSide = Math.min(w, h);
    var longSide = Math.max(w, h);
    if (shortSide < 8 || shortSide > 39 || longSide < 10 || longSide > 72) return null;
    var d = params.get('d');
    var depth;
    if (d === null || d === '') depth = longSide >= 30 ? '1 1/2 Inch' : '3/4 Inch';
    else if (Object.prototype.hasOwnProperty.call(CURATOR_DEPTHS, d)) depth = CURATOR_DEPTHS[d];
    else return null;
    return { id: id.toLowerCase(), exp: exp, sig: sig, w: w, h: h, depth: depth };
  }

  function urlWithoutCuratorParams() {
    var params = new URLSearchParams(window.location.search);
    CURATOR_PARAMS.forEach(function (k) { params.delete(k); });
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
  }

  function blobToDataURL(blob) {
    return new Promise(function (resolve) {
      var reader = new FileReader();
      reader.onload = function () { resolve(String(reader.result || '')); };
      reader.onerror = function () { resolve(null); };
      reader.readAsDataURL(blob);
    });
  }

  var docSettled = document.readyState === 'complete';
  if (!docSettled) {
    var markSettled = function () { docSettled = true; };
    document.addEventListener('DOMContentLoaded', markSettled, { once: true });
    window.addEventListener('load', markSettled, { once: true });
  }
  function whenDocumentSettled(fn) {
    if (docSettled || document.readyState === 'complete') { fn(); return; }
    var ran = false;
    var run = function () {
      if (ran) return;
      ran = true;
      fn();
    };
    document.addEventListener('DOMContentLoaded', run, { once: true });
    window.addEventListener('load', run, { once: true });
  }

  var LIVE_MQ = 'screen and (min-width: 990px)';
  var LIVE_FRAME_MS = 1000 / 30;
  var LIVE_SRC_EDGE = 120;
  var LIVE_GRID = 8;
  var LIVE_JITTER = (50 * Math.PI) / 180;
  var LIVE_TAU = Math.PI * 2;
  var LIVE_LINEN = '#ebe3d2';
  var LIVE_WEAVE = '#8a7f69';
  var LIVE_GRAPHITE = '#34332f';
  var LIVE_ASPECTS = { landscape: 3 / 2, portrait: 2 / 3, square: 1 };
  var LIVE_AR_MIN = 2 / 3;
  var LIVE_AR_MAX = 3 / 2;
  var LIVE_STAGE_ORDER = { queued: 0, handoff: 1, checking: 2, painting: 3, finishing: 4 };
  var LIVE_STEPS = ['checking', 'painting', 'finishing'];

  var LIVE_PALETTES = [
    { words: ['dawn', 'sunrise', 'sunset', 'dusk', 'golden'],
      sky: ['#f7cf8a', '#f1a383', '#d47f8f'], blobs: ['#ffdca3', '#ec8c62', '#b8607e', '#fbe7c0', '#8a6390'] },
    { words: ['night', 'nights', 'moon', 'moonlit', 'moonlight', 'star', 'stars', 'starry', 'midnight'],
      sky: ['#0e1838', '#232c63', '#39417c'], blobs: ['#c8d0e0', '#4a4f9c', '#18244f', '#eef1f7', '#6b72b5'] },
    { words: ['snow', 'snowy', 'winter', 'ice', 'icy', 'frost', 'frosty'],
      sky: ['#dce7f1', '#f5f8fb', '#c7d8e8'], blobs: ['#ffffff', '#a8c3dc', '#8daecb', '#e7eff6', '#6c8cab'] },
    { words: ['forest', 'forests', 'tree', 'trees', 'woods', 'woodland', 'jungle', 'leaf', 'leaves'],
      sky: ['#a9c193', '#557f4d', '#2c472d'], blobs: ['#6f9b5a', '#c6d89e', '#3a5d39', '#8a7b4b', '#a7c47c'] },
    { words: ['sea', 'seas', 'ocean', 'harbour', 'harbor', 'lake', 'river', 'beach', 'coast', 'coastal', 'wave', 'waves', 'seaside'],
      sky: ['#bddbe6', '#4e97af', '#e5d2a6'], blobs: ['#2e7387', '#80c1ca', '#1f4e6a', '#f1e1bb', '#59a8b4'] },
    { words: ['desert', 'sand', 'sandy', 'dune', 'dunes', 'canyon'],
      sky: ['#f3d8a4', '#d9a25d', '#ad6d39'], blobs: ['#e9b978', '#c8894a', '#8d5531', '#f6e3ba', '#a3673d'] },
    { words: ['flower', 'flowers', 'poppy', 'poppies', 'rose', 'roses', 'garden', 'gardens', 'bloom', 'blooms', 'blossom', 'tulip', 'tulips'],
      sky: ['#f4e9da', '#ebbcbc', '#6e8f4d'], blobs: ['#d63b3b', '#e8738b', '#f3a3b4', '#5e8a44', '#c42f46'] },
    { words: ['city', 'street', 'streets', 'urban', 'neon'],
      sky: ['#3b4352', '#596274', '#292e37'], blobs: ['#ff4fa3', '#3ddfe0', '#7b8597', '#ffd166', '#8a5cff'] },
    { words: ['autumn', 'fall', 'maple'],
      sky: ['#f1ca8b', '#d27a3b', '#793d1f'], blobs: ['#e1912f', '#b4472a', '#894a2a', '#f3b55d', '#6a3922'] },
    { words: ['mountain', 'mountains', 'alps', 'alpine', 'peak', 'peaks'],
      sky: ['#c9d4e0', '#8b9aab', '#5c6a79'], blobs: ['#ffffff', '#a6b3c1', '#6e7e8f', '#eaeff4', '#4e5d6c'] }
  ];

  var LIVE_PALETTE_DEFAULT = { sky: ['#e9e1cf', '#cfc4a9', '#8f9b7f'], blobs: ['#a3b18a', '#c08a6a', '#5d6b78', '#e5d7b9', '#7f8f6a'] };
  LIVE_PALETTES.forEach(function (p) {
    p.re = new RegExp('\\b(?:' + p.words.join('|') + ')\\b', 'i');
  });

  function hashString(str) {
    var h = 2166136261;
    var s = String(str == null ? '' : str);
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  function seededRandom(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function hexToRgba(hex, alpha) {
    var n = parseInt(String(hex).slice(1), 16) || 0;
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
  }

  function livePalettesFor(text) {
    var hits = [];
    LIVE_PALETTES.forEach(function (p) {
      var m = p.re.exec(text);
      if (m) hits.push({ at: m.index, palette: p });
    });
    hits.sort(function (a, b) { return a.at - b.at; });
    return hits.map(function (h) { return h.palette; });
  }

  function liveSourceDims(ar) {
    return ar >= 1
      ? { w: LIVE_SRC_EDGE, h: Math.max(1, Math.round(LIVE_SRC_EDGE / ar)) }
      : { w: Math.max(1, Math.round(LIVE_SRC_EDGE * ar)), h: LIVE_SRC_EDGE };
  }

  function liveReadSource(ctx, w, h, rand) {
    var px;
    try {
      px = ctx.getImageData(0, 0, w, h).data;
    } catch (err) {
      return null;
    }
    var n = w * h;
    var lum = new Float32Array(n);
    for (var i = 0, p = 0; i < n; i++, p += 4) {
      lum[i] = (0.2126 * px[p] + 0.7152 * px[p + 1] + 0.0722 * px[p + 2]) / 255;
    }
    var cells = LIVE_GRID * LIVE_GRID;
    var jxx = new Float32Array(cells);
    var jyy = new Float32Array(cells);
    var jxy = new Float32Array(cells);
    for (var y = 1; y < h - 1; y++) {
      for (var x = 1; x < w - 1; x++) {
        var k = y * w + x;
        var gx = lum[k + 1] - lum[k - 1];
        var gy = lum[k + w] - lum[k - w];
        var cell = ((y * LIVE_GRID / h) | 0) * LIVE_GRID + ((x * LIVE_GRID / w) | 0);
        jxx[cell] += gx * gx;
        jyy[cell] += gy * gy;
        jxy[cell] += gx * gy;
      }
    }
    var angles = new Float32Array(cells);
    var flat = (w * h) / cells * 0.0004;
    for (var c = 0; c < cells; c++) {
      angles[c] = jxx[c] + jyy[c] > flat
        ? 0.5 * Math.atan2(2 * jxy[c], jxx[c] - jyy[c]) + Math.PI / 2
        : (rand() - 0.5) * 0.6;
    }
    return {
      w: w,
      h: h,
      px: px,
      lum: lum,
      angles: angles,
      edgeRight: liveEdgeColor(px, w, w - 1, 0, 0, 1, h),
      edgeBottom: liveEdgeColor(px, w, 0, h - 1, 1, 0, w)
    };
  }

  function liveEdgeColor(px, w, fromX, fromY, stepX, stepY, count) {
    var r = 0;
    var g = 0;
    var b = 0;
    for (var s = 0; s < count; s++) {
      var q = ((fromY + s * stepY) * w + (fromX + s * stepX)) * 4;
      r += px[q];
      g += px[q + 1];
      b += px[q + 2];
    }
    return 'rgb(' + Math.round(r / count) + ',' + Math.round(g / count) + ',' + Math.round(b / count) + ')';
  }

  function liveDescribeSource(prompt, ar, rand) {
    var d = liveSourceDims(ar);
    var c = document.createElement('canvas');
    c.width = d.w;
    c.height = d.h;
    var ctx = c.getContext('2d');
    if (!ctx) return null;
    var picks = livePalettesFor(normalizePrompt(prompt));
    var main = picks[0] || LIVE_PALETTE_DEFAULT;
    var tint = picks[1] || main;
    var sky = ctx.createLinearGradient(0, 0, 0, d.h);
    sky.addColorStop(0, main.sky[0]);
    sky.addColorStop(0.35 + rand() * 0.3, main.sky[1]);
    sky.addColorStop(1, main.sky[2]);
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, d.w, d.h);
    var long = Math.max(d.w, d.h);
    var count = 4 + Math.floor(rand() * 3);
    for (var i = 0; i < count; i++) {
      var pal = i % 3 === 2 ? tint : main;
      var col = pal.blobs[Math.floor(rand() * pal.blobs.length)];
      var x = rand() * d.w;
      var y = rand() * d.h;
      var r = long * (0.16 + rand() * 0.3);
      var blob = ctx.createRadialGradient(x, y, 0, x, y, r);
      blob.addColorStop(0, hexToRgba(col, 0.6 + rand() * 0.3));
      blob.addColorStop(1, hexToRgba(col, 0));
      ctx.fillStyle = blob;
      ctx.fillRect(0, 0, d.w, d.h);
    }
    return liveReadSource(ctx, d.w, d.h, rand);
  }

  function livePhotoSource(img, ar, rand) {
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    if (!iw || !ih) return null;
    var d = liveSourceDims(ar);
    var c = document.createElement('canvas');
    c.width = d.w;
    c.height = d.h;
    var ctx = c.getContext('2d');
    if (!ctx) return null;
    var sw = iw;
    var sh = iw / ar;
    if (sh > ih) {
      sh = ih;
      sw = ih * ar;
    }
    ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, d.w, d.h);
    return liveReadSource(ctx, d.w, d.h, rand);
  }

  function livePhase(stage) {
    if (stage === 'finishing') return 'finish';
    if (stage === 'painting') return 'paint';
    return 'sketch';
  }

  function LivePainter(canvas, ctx, opts) {
    var self = this;
    this.canvas = canvas;
    this.ctx = ctx;
    this.rand = opts.rand;
    this.reduced = !!opts.reduced;
    this.src = null;
    this.phase = 'sketch';
    this.progress = 0;
    this.w = 0;
    this.h = 0;
    this.dpr = 1;
    this.perFrame = 4;
    this.raf = 0;
    this.last = 0;
    this.frames = 0;
    this.inView = true;
    this.painted = false;

    this.paintedTo = Infinity;
    this.settleTimer = 0;
    this.burstDirty = false;
    this.scratch = null;
    this.alive = true;
    this.tick = function (now) { self.frame(now); };
    this.onVisibility = function () { self.sync(); };
    this.onSettle = function () { self.settle(); };
    document.addEventListener('visibilitychange', this.onVisibility);
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(function () { self.onResize(); });
      this.ro.observe(canvas);
    }
    if (window.IntersectionObserver) {
      this.io = new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        self.inView = !!(entry && entry.isIntersecting);
        self.sync();
      });
      this.io.observe(opts.observe || canvas);
    }
    this.resize();
  }

  LivePainter.prototype.shouldRun = function () {
    return this.alive && !this.reduced && !!this.src && this.inView && !document.hidden && this.w > 0;
  };

  LivePainter.prototype.sync = function () {
    if (!this.alive) return;
    if (this.reduced) {
      if (this.src && this.w && !this.painted) this.staticFrame();
      return;
    }
    if (this.shouldRun()) {
      if (!this.raf) {
        this.resume();
        this.raf = requestAnimationFrame(this.tick);
      }
    } else if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }
  };

  LivePainter.prototype.resume = function () {
    if (this.phase === 'sketch' || !this.src || !this.w) return;
    var finest = this.brush() / Math.min(this.w, this.h);
    if (finest >= this.paintedTo * 0.68) return;
    this.rebuild(finest, Math.min(0.09, this.paintedTo));
  };

  LivePainter.prototype.setSource = function (src) {
    if (!this.alive || !src) return;
    this.src = src;
    this.catchUp();
    this.sync();
  };

  LivePainter.prototype.setPhase = function (phase, progress) {
    this.phase = phase;
    this.progress = clamp(progress || 0, 0, 1);
  };

  LivePainter.prototype.onResize = function () {
    if (!this.alive) return;
    var burst = !!this.settleTimer;
    if (!this.resize(burst)) return;
    if (burst) {
      clearTimeout(this.settleTimer);
      this.burstDirty = true;
    }
    this.settleTimer = setTimeout(this.onSettle, 150);
  };

  LivePainter.prototype.settle = function () {
    this.settleTimer = 0;
    this.scratch = null;
    if (!this.alive || !this.burstDirty) return;
    this.burstDirty = false;
    this.painted = false;
    this.clear();
    this.catchUp();
    this.sync();
  };

  LivePainter.prototype.resize = function (rescale) {
    if (!this.alive) return false;

    var w = this.canvas.clientWidth;
    var h = this.canvas.clientHeight;
    if (w < 2 || h < 2) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (w === this.w && h === this.h && dpr === this.dpr) return false;
    var kept = rescale && this.w > 0 ? this.keepPaint() : null;
    this.w = w;
    this.h = h;
    this.dpr = dpr;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.perFrame = clamp(Math.round((w * h) / 9000), 4, 24);
    if (kept) {
      var paintedTo = this.paintedTo;
      this.clear();
      this.ctx.drawImage(kept, 0, 0, kept.width, kept.height, 0, 0, w, h);
      this.paintedTo = paintedTo;
    } else {
      this.painted = false;
      this.clear();
      this.catchUp();
    }
    this.sync();
    return true;
  };

  LivePainter.prototype.keepPaint = function () {
    var c = this.scratch || (this.scratch = document.createElement('canvas'));
    c.width = this.canvas.width;
    c.height = this.canvas.height;
    var ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(this.canvas, 0, 0);
    return c;
  };

  LivePainter.prototype.clear = function () {
    var ctx = this.ctx;
    var w = this.w;
    var h = this.h;
    this.paintedTo = Infinity;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.globalAlpha = 1;
    ctx.fillStyle = LIVE_LINEN;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = 0.07;
    ctx.strokeStyle = LIVE_WEAVE;
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    for (var x = 1.5; x < w; x += 3) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
    for (var y = 1.5; y < h; y += 3) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  LivePainter.prototype.catchUp = function () {
    if (!this.src || !this.w) return;
    if (this.reduced) {
      this.staticFrame();
      return;
    }
    if (this.phase === 'sketch') this.strokes(this.perFrame * 12);
    else this.rebuild(this.brush() / Math.min(this.w, this.h));
  };

  LivePainter.prototype.rebuild = function (finest, from) {
    var area = this.w * this.h;
    var shortEdge = Math.min(this.w, this.h);
    var i;
    if (from == null) {
      this.ctx.strokeStyle = LIVE_GRAPHITE;
      for (i = 0; i < clamp(Math.round(area / 300), 120, 360); i++) this.sketchStroke();
      from = 0.09;
    }
    for (var f = from; f >= finest - 1e-6; f *= 0.68) {
      var r = shortEdge * f;
      var count = clamp(Math.round(area / (2.6 * r * r)), 40, 600);
      for (i = 0; i < count; i++) this.paintStroke(r);
      if (f < this.paintedTo) this.paintedTo = f;
    }
    this.ctx.globalAlpha = 1;
  };

  LivePainter.prototype.frame = function (now) {
    this.raf = 0;
    if (!this.shouldRun()) return;
    this.raf = requestAnimationFrame(this.tick);
    if (now - this.last < LIVE_FRAME_MS - 1) return;
    this.last = now;
    this.frames++;
    if (this.phase === 'sketch') {
      this.strokes(this.perFrame);

      if (this.frames % 40 === 0) this.wash(0.04);
    } else {
      this.strokes(this.perFrame);
    }
  };

  LivePainter.prototype.wash = function (alpha) {
    var ctx = this.ctx;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = LIVE_LINEN;
    ctx.fillRect(0, 0, this.w, this.h);
    ctx.globalAlpha = 1;
  };

  LivePainter.prototype.brush = function () {
    var shortEdge = Math.min(this.w, this.h);
    if (this.phase === 'finish') return shortEdge * 0.012;

    return shortEdge * (0.09 + (0.012 - 0.09) * this.progress);
  };

  LivePainter.prototype.strokes = function (n) {
    if (!this.src || !this.w) return;
    var i;
    if (this.phase === 'sketch') {
      this.ctx.strokeStyle = LIVE_GRAPHITE;
      for (i = 0; i < n; i++) this.sketchStroke();
    } else {
      var r = this.brush();
      for (i = 0; i < n; i++) this.paintStroke(r);
      var f = r / Math.min(this.w, this.h);
      if (f < this.paintedTo) this.paintedTo = f;
    }
    this.ctx.globalAlpha = 1;
  };

  LivePainter.prototype.srcIndex = function (x, y) {
    var s = this.src;
    var sx = (x / this.w * s.w) | 0;
    var sy = (y / this.h * s.h) | 0;
    if (sx >= s.w) sx = s.w - 1;
    if (sy >= s.h) sy = s.h - 1;
    return sy * s.w + sx;
  };

  LivePainter.prototype.angleAt = function (x, y) {
    var gx = (x / this.w * LIVE_GRID) | 0;
    var gy = (y / this.h * LIVE_GRID) | 0;
    if (gx >= LIVE_GRID) gx = LIVE_GRID - 1;
    if (gy >= LIVE_GRID) gy = LIVE_GRID - 1;
    return this.src.angles[gy * LIVE_GRID + gx] + (this.rand() - 0.5) * LIVE_JITTER;
  };

  LivePainter.prototype.sketchStroke = function () {
    var rand = this.rand;
    var ctx = this.ctx;
    var x = rand() * this.w;
    var y = rand() * this.h;
    var dark = 1 - this.src.lum[this.srcIndex(x, y)];
    if (rand() > 0.06 + dark * dark * 1.1) return;
    var a = this.angleAt(x, y);
    var cos = Math.cos(a);
    var sin = Math.sin(a);
    var half = Math.min(this.w, this.h) * (0.015 + rand() * 0.035);
    var bend = (rand() - 0.5) * half * 0.6;
    var lines = 1 + Math.floor(rand() * 3);
    ctx.globalAlpha = 0.12 + rand() * 0.2;
    ctx.lineWidth = 0.5 + rand() * 0.7;
    ctx.beginPath();
    for (var k = 0; k < lines; k++) {
      var ox = -sin * k * 2.2;
      var oy = cos * k * 2.2;
      ctx.moveTo(x - cos * half + ox, y - sin * half + oy);
      ctx.quadraticCurveTo(x - sin * bend + ox, y + cos * bend + oy, x + cos * half + ox, y + sin * half + oy);
    }
    ctx.stroke();
  };

  LivePainter.prototype.paintStroke = function (r) {
    var rand = this.rand;
    var ctx = this.ctx;
    var px = this.src.px;
    var x = rand() * this.w;
    var y = rand() * this.h;
    var p = this.srcIndex(x, y) * 4;
    var j = 0.92 + rand() * 0.16;
    var cr = px[p];
    var cg = px[p + 1];
    var cb = px[p + 2];
    var a = this.angleAt(x, y);
    var size = r * (0.7 + rand() * 0.6);
    var cos = Math.cos(a);
    var sin = Math.sin(a);
    var half = size * (1.2 + rand() * 0.9);
    var alpha = 0.5 + rand() * 0.35;
    ctx.globalAlpha = alpha;
    var color = 'rgb(' + (Math.min(255, cr * j) | 0) + ',' + (Math.min(255, cg * j) | 0) + ',' + (Math.min(255, cb * j) | 0) + ')';
    if (rand() < 0.5) {
      var bend = (rand() - 0.5) * size;
      ctx.strokeStyle = color;
      ctx.lineWidth = size * 0.8;
      ctx.beginPath();
      ctx.moveTo(x - cos * half, y - sin * half);
      ctx.quadraticCurveTo(x - sin * bend, y + cos * bend, x + cos * half, y + sin * half);
      ctx.stroke();
    } else {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(x, y, half, size * (0.38 + rand() * 0.22), a, 0, LIVE_TAU);
      ctx.fill();
    }
    if (size < 3) return;
    var lift = 1.18 + rand() * 0.1;
    var off = size * 0.18;
    ctx.globalAlpha = alpha * 0.4;
    ctx.strokeStyle = 'rgb(' + (Math.min(255, cr * lift + 12) | 0) + ',' + (Math.min(255, cg * lift + 12) | 0) + ',' + (Math.min(255, cb * lift + 12) | 0) + ')';
    ctx.lineWidth = Math.max(0.6, size * 0.14);
    ctx.beginPath();
    ctx.moveTo(x - cos * half * 0.75 + sin * off, y - sin * half * 0.75 - cos * off);
    ctx.lineTo(x + cos * half * 0.75 + sin * off, y + sin * half * 0.75 - cos * off);
    ctx.stroke();
  };

  LivePainter.prototype.staticFrame = function () {
    if (!this.src || !this.w) return;
    this.painted = true;
    this.clear();
    this.rebuild(0.03);
  };

  LivePainter.prototype.destroy = function () {
    this.alive = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = 0;
    if (this.ro) this.ro.disconnect();
    if (this.io) this.io.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.ro = null;
    this.io = null;
    this.src = null;
    this.ctx = null;
    this.canvas = null;
    this.scratch = null;
    this.rand = null;
    this.tick = null;
    this.onVisibility = null;
    this.onSettle = null;
  };

  class AIPortraitStudio extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.serviceUrl = (this.dataset.serviceUrl || '').replace(/\/+$/, '');
      if (!this.serviceUrl) return;

      var strEl = this.querySelector('[data-aips-strings]');
      var parsed = {};
      try {
        parsed = strEl ? JSON.parse(strEl.textContent) : {};
      } catch (err) {
        parsed = {};
      }
      this.strings = parsed;
      this.customer = parsed.customer || null;
      this.stageCopies = parsed.stages || {};

      var self0 = this;
      this.imageDataURL = null;
      this.resultDataURL = null;

      this.resultBase = null;
      this.edit = { pos: 'Bottom Right', font: 'Caveat', color: '#ffffff', sizePct: 100, bright: 100, contrast: 100, sat: 100 };
      this.resultView = '3d';

      this.sessionArtworks = [];
      this.stagedArtwork = 0;
      this.styles = [];
      this.styleById = {};
      this.selectedStyle = null;
      this.filterText = '';
      this.activeGroup = 'all';
      this.quota = null;
      this.serviceDown = false;
      this.imagegenOffline = false;
      this.busy = false;
      this.jobId = null;
      this.pollTimer = null;
      this.pollFails = 0;
      this.pollStart = 0;

      this.jobImageDataURL = null;
      this.jobStyle = null;

      this.jobMode = null;
      this.jobPrompt = '';
      this.jobAspect = '';

      this.mode = 'photo';
      this.describeEnabled = false;
      this.featureLocal = false;
      this.aspect = 'landscape';
      this.autoStyle = { id: AUTO_STYLE_ID, auto: true, displayTitle: parsed.styleAuto || '', group: 'auto' };

      this.selectedStyleByMode = { photo: null, describe: this.autoStyle };
      this.errorAction = 'retry';
      this._countBand = 0;
      this.requestedMode = null;
      try {
        if (new URLSearchParams(window.location.search).get('mode') === 'describe') this.requestedMode = 'describe';
      } catch (err) {
        this.requestedMode = null;
      }

      this.stage = 'queued';
      this.etaS = DEFAULT_ETA_S;
      this.elapsedS = 0;
      this.tickBase = 0;
      this.tickTimer = null;
      this.progressHint = 0;

      this.quizActive = false;
      this.quizPool = null;
      this.quizTimer = null;
      this.quizStreak = 0;
      this.quizLastId = null;
      this.quizCorrectItem = null;
      this.cut = 50;

      this.liveStrings = parsed.live || {};
      this.live = {
        active: false, done: false, jobId: null, token: 0, mode: 'photo',
        stage: 'queued', pct: -1, ar: LIVE_ASPECTS.landscape, seed: 0,
        source: null, sourcePending: false, stepKey: '', swept: false,
        doneImage: '', doneShown: '', doneFit: false
      };
      this.livePainter = null;

      if (window.AirXAISession) window.AirXAISession.token();

      this.grabRefs();
      this.bindUpload();
      this.bindActions();
      this.bindDescribe();
      this.bindLivePanel();
      this.initCompare();

      this.boot();

      if (!this.maybeLoadCuratorArt()) this.maybeLoadFromHash();
    }

    disconnectedCallback() {

      this.stopPolling();
      this.stopTicker();
      this.stopQuiz();
      this.liveHide();
      this.releaseBusy();
    }

    api(path) {
      return this.serviceUrl + path;
    }

    authHeaders(base) {
      if (!window.AirXAISession) return Promise.resolve(base || {});
      return window.AirXAISession.token().then(function (tok) {
        return window.AirXAISession.headers(tok, base);
      });
    }

    grabRefs() {
      this.quotaWrap = this.querySelector('[data-aips-quota]');
      this.quotaPill = this.querySelector('[data-aips-quota-pill]');

      this.livePanel = this.querySelector('[data-aips-livepanel]');
      var lp = this.livePanel;
      this.liveFrame = lp && lp.querySelector('[data-aips-live-frame]');
      this.liveCanvas = lp && lp.querySelector('[data-aips-live-canvas]');
      this.liveResult = lp && lp.querySelector('[data-aips-live-result]');
      this.liveCaption = lp && lp.querySelector('[data-aips-live-caption]');
      this.liveSteps = lp ? Array.prototype.slice.call(lp.querySelectorAll('[data-aips-live-step]')) : [];
      this.liveQueueEl = lp && lp.querySelector('[data-aips-live-queue]');
      this.livePct = lp && lp.querySelector('[data-aips-live-pct]');
      this.liveBar = lp && lp.querySelector('[data-aips-live-bar]');
      this.liveElapsed = lp && lp.querySelector('[data-aips-live-elapsed]');
      this.liveDoneEl = lp && lp.querySelector('[data-aips-live-done]');
      this.liveSeeBtn = lp && lp.querySelector('[data-aips-live-see]');

      this.dropzone = this.querySelector('[data-aips-dropzone]');
      this.fileInput = this.querySelector('[data-aips-file]');
      this.browseBtn = this.querySelector('[data-aips-browse]');
      this.uploadPrompt = this.querySelector('[data-aips-upload-prompt]');
      this.previewWrap = this.querySelector('[data-aips-preview]');
      this.previewImg = this.querySelector('[data-aips-preview-img]');
      this.replaceBtn = this.querySelector('[data-aips-replace]');
      this.uploadError = this.querySelector('[data-aips-upload-error]');

      this.modeList = this.querySelector('[data-aips-mode]');
      this.modeTabs = Array.prototype.slice.call(this.querySelectorAll('[data-aips-mode-tab]'));
      this.modePanels = Array.prototype.slice.call(this.querySelectorAll('[data-aips-panel]'));
      this.describePanel = this.querySelector('[data-aips-panel="describe"]');
      this.promptInput = this.querySelector('[data-aips-prompt]');
      this.promptCount = this.querySelector('[data-aips-prompt-count]');
      this.promptError = this.querySelector('[data-aips-prompt-error]');
      this.aspectInputs = Array.prototype.slice.call(this.querySelectorAll('[data-aips-aspect]'));
      this.privacyText = this.querySelector('[data-aips-privacy-text]');

      this.privacyDefault = this.privacyText ? this.privacyText.textContent.trim() : '';
      this.autoIconTpl = this.querySelector('template[data-aips-auto-icon]');

      this.railEl = this.querySelector('[data-aips-styles]');
      this.railSkeleton = this.querySelector('[data-aips-styles-skeleton]');
      this.stylesError = this.querySelector('[data-aips-styles-error]');
      this.stylesRetry = this.querySelector('[data-aips-styles-retry]');
      this.stylesEmpty = this.querySelector('[data-aips-styles-empty]');
      this.filterWrap = this.querySelector('[data-aips-filter-wrap]');
      this.filterInput = this.querySelector('[data-aips-filter]');
      this.chipsWrap = this.querySelector('[data-aips-chips]');

      this.generateBtn = this.querySelector('[data-aips-generate]');
      this.hintEl = this.querySelector('[data-aips-hint]');
      this.liveEl = this.querySelector('[data-aips-live]');

      this.progressCard = this.querySelector('[data-aips-progress]');
      this.progressImg = this.querySelector('[data-aips-progress-img]');
      this.progressMedia = this.querySelector('[data-aips-progress-media]');
      this.promptQuoteWrap = this.querySelector('[data-aips-prompt-quote-wrap]');
      this.promptQuote = this.querySelector('[data-aips-prompt-quote]');
      this.stageLine = this.querySelector('[data-aips-stage-line]');
      this.queueEl = this.querySelector('[data-aips-queue]');
      this.barEl = this.querySelector('[data-aips-bar]');
      this.elapsedEl = this.querySelector('[data-aips-elapsed]');

      this.quizWrap = this.querySelector('[data-aips-quiz]');
      this.quizImg = this.querySelector('[data-aips-quiz-img]');
      this.quizOptions = this.querySelector('[data-aips-quiz-options]');
      this.quizFeedback = this.querySelector('[data-aips-quiz-feedback]');
      this.quizStreakEl = this.querySelector('[data-aips-quiz-streak]');

      this.quotaCard = this.querySelector('[data-aips-quota-card]');
      this.quotaTitle = this.querySelector('[data-aips-quota-title]');

      this.offlineCard = this.querySelector('[data-aips-offline]');
      this.offlineRetry = this.querySelector('[data-aips-offline-retry]');

      this.lateCard = this.querySelector('[data-aips-late]');

      this.errorCard = this.querySelector('[data-aips-error]');
      this.errorMsg = this.querySelector('[data-aips-error-msg]');
      this.errorTitleEl = this.querySelector('[data-aips-error-title]');
      this.errorRetry = this.querySelector('[data-aips-error-retry]');

      this.revealCard = this.querySelector('[data-aips-reveal]');
      this.compareEl = this.querySelector('[data-aips-compare]');
      this.beforeImg = this.querySelector('[data-aips-before]');
      this.afterImg = this.querySelector('[data-aips-after]');
      this.handleEl = this.querySelector('[data-aips-handle]');
      this.canvas3dHost = this.querySelector('[data-aips-canvas3d]');
      this.personalInput = this.querySelector('[data-aips-personal]');
      this.personalPos = this.querySelector('[data-aips-personal-pos]');
      this.personalFont = this.querySelector('[data-aips-personal-font]');
      this.personalColors = this.querySelector('[data-aips-personal-colors]');
      this.sizeMinus = this.querySelector('[data-aips-size-minus]');
      this.sizePlus = this.querySelector('[data-aips-size-plus]');
      this.sizeVal = this.querySelector('[data-aips-size-val]');
      this.adjWrap = this.querySelector('[data-aips-adjust]');
      this.adjReset = this.querySelector('[data-aips-adj-reset]');
      this.flatWrap = this.querySelector('[data-aips-flat]');
      this.flatImg = this.querySelector('[data-aips-flat-img]');
      this.loupeEl = this.querySelector('[data-aips-loupe]');
      this.viewBtns = Array.prototype.slice.call(this.querySelectorAll('[data-aips-view]'));
      this.galleryEl = this.querySelector('[data-aips-gallery]');
      this.galleryListEl = this.querySelector('[data-aips-gallery-list]');
      this.printBtn = this.querySelector('[data-aips-print]');
      this.anotherBtn = this.querySelector('[data-aips-another]');
      this.downloadLink = this.querySelector('[data-aips-download]');

      this.stageCards = [this.progressCard, this.quotaCard, this.offlineCard, this.lateCard, this.errorCard, this.revealCard];
    }

    announce(text) {
      if (this.liveEl && text) this.liveEl.textContent = text;
    }

    boot() {
      var self = this;
      this.serviceDown = false;
      this.hideStage();

      this.healthCheck()
        .then(function (health) {

          var painter = health && (health.imagegen || health.watcher || health.studio);
          self.imagegenOffline = !!(painter && painter.available === false);

          self.applyFeatures(health && health.features);
          self.loadStyles();
          self.loadQuota();
          self.updateReadiness();
        })
        .catch(function () {

          self.serviceDown = true;
          self.imagegenOffline = true;
          self.showStylesError();
          self.showOffline(true);
          self.updateReadiness();
        });
    }

    applyFeatures(features) {
      var f = features && typeof features === 'object' ? features : {};
      this.featureLocal = f.local === true;
      var describe = f.describe === true && !!this.modeList && !!this.describePanel && this.modeTabs.length === 2;
      this.describeEnabled = describe;
      if (this.modeList) this.modeList.hidden = !describe;

      this.modePanels.forEach(function (panel) {
        if (describe) {
          panel.setAttribute('role', 'tabpanel');
          if (panel.dataset.aipsTabId) panel.setAttribute('aria-labelledby', panel.dataset.aipsTabId);
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
        }
      });
      if (!describe) {
        if (this.mode !== 'photo') this.setMode('photo');
      } else if (this.requestedMode === 'describe' && !this._deepLinkUsed) {
        this._deepLinkUsed = true;
        this.setMode('describe');
      }
      this.updatePrivacy();
    }

    healthCheck() {
      var self = this;
      var probe = function () {
        return fetchJSON(self.api('/api/health'), { timeout: HEALTH_TIMEOUT });
      };
      return probe().catch(function () { return probe(); });
    }

    loadStyles() {
      var self = this;
      if (this.stylesError) this.stylesError.hidden = true;
      if (this.railSkeleton) this.railSkeleton.hidden = false;

      fetchJSON(this.api('/api/styles'), { timeout: MISC_TIMEOUT })
        .then(function (data) {
          var list = Array.isArray(data) ? data : (data && data.styles) || [];
          self.styles = list.filter(function (s) { return s && s.id; });
          if (!self.styles.length) {

            self.showStylesError();
            return;
          }
          self.renderStyles();
        })
        .catch(function () {
          self.showStylesError();
        });
    }

    showStylesError() {
      if (this.railSkeleton) this.railSkeleton.hidden = true;
      if (this.stylesError) this.stylesError.hidden = false;
    }

    renderStyles() {
      var self = this;
      if (this.railSkeleton) this.railSkeleton.hidden = true;

      Array.from(this.railEl.children).forEach(function (child) {
        if (!child.hasAttribute('data-aips-styles-skeleton')) child.remove();
      });

      this.styleById = {};

      if (this.describeEnabled) {
        this.styleById[AUTO_STYLE_ID] = this.autoStyle;
        this.railEl.appendChild(this.buildAutoTile());
      }
      this.styles.forEach(function (style) {
        style.displayTitle = titleCase(style.title || String(style.id).replace(/-/g, ' '));
        style.group = groupForStyle(style.id);
        if (self.describeEnabled && String(style.id) === AUTO_STYLE_ID) return;
        self.styleById[String(style.id)] = style;
        self.railEl.appendChild(self.buildTile(style));
      });
      this._stylesRendered = true;

      this.setupStyleFilters();
      this.applyStyleFilter();
      this.syncTileSelection();

      if (motionOn()) {
        window.gsap.fromTo(
          this.railEl.querySelectorAll('.aips-tile'),
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out', stagger: 0.03, clearProps: 'opacity,transform' }
        );
      }
    }

    buildTile(style) {
      var self = this;
      var tile = document.createElement('button');
      tile.type = 'button';
      tile.dataset.styleId = String(style.id);
      tile.setAttribute('aria-pressed', 'false');
      tile.className = 'aips-tile ' + (style.sampleUrl ? 'aips-tile--sample' : 'aips-tile--type');

      if (style.sampleUrl) {
        var img = document.createElement('img');
        img.className = 'aips-tile__img';
        img.src = this.serviceUrl + style.sampleUrl;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 240;
        img.height = 180;
        img.addEventListener('error', function () {

          tile.classList.remove('aips-tile--sample');
          tile.classList.add('aips-tile--type');
          img.remove();
          if (!tile.querySelector('.aips-tile__initial')) {
            tile.insertBefore(self.buildTileInitial(style.displayTitle), tile.firstChild);
          }
        });
        tile.appendChild(img);
      } else {
        tile.appendChild(this.buildTileInitial(style.displayTitle));
      }

      var meta = document.createElement('span');
      meta.className = 'aips-tile__meta';
      var label = document.createElement('span');
      label.className = 'aips-tile__label';
      label.textContent = style.displayTitle;
      meta.appendChild(label);
      if (typeof style.promptCount === 'number' && style.promptCount > 1 && this.strings.stylesCount) {
        var count = document.createElement('span');
        count.className = 'aips-tile__count';
        count.textContent = interp(this.strings.stylesCount, { n: String(style.promptCount) });
        meta.appendChild(count);
      }
      tile.appendChild(meta);

      tile.addEventListener('click', function () { self.selectStyle(style, tile); });
      return tile;
    }

    buildAutoTile() {
      var self = this;
      var style = this.autoStyle;
      var tile = document.createElement('button');
      tile.type = 'button';
      tile.dataset.styleId = AUTO_STYLE_ID;
      tile.setAttribute('aria-pressed', 'false');
      tile.className = 'aips-tile aips-tile--type aips-tile--auto';
      tile.hidden = this.mode !== 'describe';

      var mark = document.createElement('span');
      mark.className = 'aips-tile__initial';
      mark.setAttribute('aria-hidden', 'true');
      if (this.autoIconTpl && this.autoIconTpl.content) {
        mark.appendChild(this.autoIconTpl.content.cloneNode(true));
      }
      tile.appendChild(mark);

      var meta = document.createElement('span');
      meta.className = 'aips-tile__meta';
      var label = document.createElement('span');
      label.className = 'aips-tile__label';
      label.textContent = style.displayTitle;
      meta.appendChild(label);
      if (this.strings.styleAutoNote) {
        var note = document.createElement('span');
        note.className = 'aips-tile__count';
        note.textContent = this.strings.styleAutoNote;
        meta.appendChild(note);
      }
      tile.appendChild(meta);

      tile.addEventListener('click', function () { self.selectStyle(style, tile); });
      return tile;
    }

    buildTileInitial(title) {
      var initial = document.createElement('span');
      initial.className = 'aips-tile__initial';
      initial.setAttribute('aria-hidden', 'true');
      initial.textContent = (String(title || '?').trim().charAt(0) || '?').toUpperCase();
      return initial;
    }

    stylesForMode() {
      if (this.mode !== 'describe') return this.styles;
      return this.styles.filter(function (s) { return s && s.describe === true; });
    }

    refreshStyleControls() {
      var pool = this.stylesForMode();
      var describe = this.mode === 'describe';
      if (this.filterWrap) this.filterWrap.hidden = describe && !pool.length;

      if (this.chipsWrap) {
        var present = {};
        pool.forEach(function (s) { present[s.group] = true; });
        var chips = this.chipsWrap.querySelectorAll('[data-aips-chip]');
        var visible = 0;
        chips.forEach(function (chip) {
          var g = chip.dataset.aipsChip;
          var show = g === 'all' || !!present[g];
          chip.hidden = !show;
          if (show) visible++;
        });
        this.chipsWrap.hidden = visible <= 2;

        if (this.activeGroup !== 'all' && !present[this.activeGroup]) this.setActiveGroup('all');
      }
    }

    setupStyleFilters() {
      var self = this;
      this.refreshStyleControls();

      if (this._filtersBound) return;
      this._filtersBound = true;

      if (this.filterInput) {
        this.filterInput.addEventListener('input', function () {
          self.filterText = self.filterInput.value.trim().toLowerCase();
          self.applyStyleFilter();
        });
      }
      if (this.chipsWrap) {
        this.chipsWrap.addEventListener('click', function (e) {
          var chip = e.target.closest('[data-aips-chip]');
          if (!chip) return;
          self.setActiveGroup(chip.dataset.aipsChip);
          self.applyStyleFilter();
        });
      }
    }

    setActiveGroup(group) {
      this.activeGroup = group || 'all';
      if (!this.chipsWrap) return;
      var active = this.activeGroup;
      this.chipsWrap.querySelectorAll('[data-aips-chip]').forEach(function (c) {
        c.setAttribute('aria-pressed', String(c.dataset.aipsChip === active));
      });
    }

    applyStyleFilter() {
      var self = this;
      var text = this.filterText || '';
      var group = this.activeGroup || 'all';
      var describe = this.mode === 'describe';
      var shown = 0;
      this.railEl.querySelectorAll('.aips-tile').forEach(function (tile) {
        var style = self.styleById[tile.dataset.styleId];
        if (style && style.auto) {

          tile.hidden = !describe;
          return;
        }
        var okMode = !describe || !!(style && style.describe === true);
        var okGroup = group === 'all' || (style && style.group === group);
        var okText =
          !text ||
          (style &&
            ((style.displayTitle || '').toLowerCase().indexOf(text) > -1 ||
              String(style.id).toLowerCase().indexOf(text) > -1));
        var show = !!(okMode && okGroup && okText);
        tile.hidden = !show;
        if (show) shown++;
      });
      if (this.stylesEmpty) {

        this.stylesEmpty.hidden = describe ? (shown > 0 || (!text && group === 'all')) : shown > 0;
      }
    }

    syncTileSelection() {
      if (!this.railEl) return;
      var id = this.selectedStyle ? String(this.selectedStyle.id) : null;
      this.railEl.querySelectorAll('.aips-tile').forEach(function (t) {
        var on = id !== null && t.dataset.styleId === id;
        t.classList.toggle('is-selected', on);
        t.setAttribute('aria-pressed', String(on));
      });
    }

    selectStyle(style, tile) {
      this.selectedStyle = style;
      this.selectedStyleByMode[this.mode] = style;
      this.railEl.querySelectorAll('.aips-tile').forEach(function (t) {
        var selected = t === tile;
        t.classList.toggle('is-selected', selected);
        t.setAttribute('aria-pressed', String(selected));
      });
      this.announce(interp(this.strings.styleSelected, { style: style.displayTitle || style.title || style.id }));
      this.updateReadiness();

      var row = this.generateBtn && this.generateBtn.closest('.aips-generate-row');
      if (row) {
        var rect = row.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < 0 || rect.bottom > vh) {
          var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          row.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
        }
      }
    }

    quotaQuery() {
      var params = new URLSearchParams();
      if (this.customer && this.customer.id != null) params.set('cid', String(this.customer.id));
      var qs = params.toString();
      return qs ? '?' + qs : '';
    }

    loadQuota() {
      var self = this;
      return this.authHeaders()
        .then(function (headers) {
          return fetchJSON(self.api('/api/quota' + self.quotaQuery()), {
            timeout: MISC_TIMEOUT, headers: headers
          });
        })
        .then(function (data) {
          self.applyQuota(data);
          return data;
        })
        .catch(function () { return null; });
    }

    applyQuota(data) {
      if (!data || !this.quotaPill) return;
      this.quota = data;
      var remaining = Math.max(0, data.remaining | 0);
      var credits = Math.max(0, data.credits | 0);

      var monthly = data.period === 'month';
      var text;
      if (data.member) {
        text = interp(this.strings.quotaMember, {
          tier: String(data.tier || ''),
          n: String(remaining),
          limit: String(data.limit | 0)
        });
      } else {
        text = interp(monthly ? this.strings.quotaLeftMonth : this.strings.quotaLeft, {
          n: String(remaining),
          limit: String(data.limit | 0)
        });
      }

      if (credits > 0 && this.strings.quotaCredits) {
        text += ' · ' + interp(this.strings.quotaCredits, { n: String(credits) });
      }
      this.quotaPill.textContent = text;
      this.quotaPill.classList.toggle('is-member', !!data.member);
      this.quotaPill.classList.toggle('is-empty', remaining <= 0 && credits <= 0);
      if (this.quotaWrap) this.quotaWrap.hidden = false;
    }

    isOutOfQuota(q) {
      if (!q || typeof q.remaining !== 'number') return false;
      return q.remaining <= 0 && (q.credits | 0) <= 0;
    }

    bindUpload() {
      var self = this;
      if (!this.dropzone || !this.fileInput) return;

      var openPicker = function () { self.fileInput.click(); };
      if (this.browseBtn) this.browseBtn.addEventListener('click', openPicker);
      if (this.replaceBtn) this.replaceBtn.addEventListener('click', openPicker);
      this.dropzone.addEventListener('click', function (e) {

        if (e.target.closest('button')) return;
        openPicker();
      });

      this.fileInput.addEventListener('change', function () {
        if (self.fileInput.files && self.fileInput.files[0]) self.readFile(self.fileInput.files[0]);
        self.fileInput.value = '';
      });

      ['dragover', 'dragenter'].forEach(function (name) {
        self.dropzone.addEventListener(name, function (e) {
          e.preventDefault();
          self.dropzone.classList.add('is-drag');
        });
      });
      ['dragleave', 'dragend'].forEach(function (name) {
        self.dropzone.addEventListener(name, function () {
          self.dropzone.classList.remove('is-drag');
        });
      });
      this.dropzone.addEventListener('drop', function (e) {
        e.preventDefault();
        self.dropzone.classList.remove('is-drag');
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) self.readFile(file);
      });
    }

    readFile(file) {
      var self = this;
      var type = String(file.type || '').toLowerCase();

      if (!/^image\//.test(type) || type === 'image/svg+xml') {
        this.setUploadError(this.strings.uploadInvalid);
        return;
      }
      if (typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES) {
        this.setUploadError(this.strings.uploadTooLarge || this.strings.uploadInvalid);
        return;
      }
      this.setUploadError('');
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        URL.revokeObjectURL(url);
        self.acceptImage(img);
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        self.setUploadError(self.strings.uploadInvalid);
      };
      img.src = url;
    }

    acceptImage(img) {
      var w = img.naturalWidth || img.width;
      var h = img.naturalHeight || img.height;
      if (!w || !h) {
        this.setUploadError(this.strings.uploadInvalid);
        return;
      }

      if (w * h > MAX_UPLOAD_PIXELS) {

        this.setUploadError(this.strings.uploadTooManyPixels || this.strings.uploadInvalid);
        return;
      }
      var scale = Math.min(1, MAX_EDGE / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * scale));
      var ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, cw, ch);

      try {
        this.imageDataURL = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
      } catch (err) {
        this.setUploadError(this.strings.uploadInvalid);
        return;
      }

      this.previewImg.src = this.imageDataURL;
      this.previewWrap.hidden = false;
      if (this.uploadPrompt) this.uploadPrompt.hidden = true;
      this.dropzone.classList.add('has-image');

      if (motionOn()) {
        window.gsap.fromTo(
          this.previewWrap,
          { opacity: 0, scale: 0.98 },
          { opacity: 1, scale: 1, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }
        );
      }

      if (this.revealCard && !this.revealCard.hidden) this.hideStage();
      this.updateReadiness();
    }

    setUploadError(text) {
      if (!this.uploadError) return;
      this.uploadError.textContent = text || '';
      this.uploadError.hidden = !text;
    }

    updateReadiness() {
      if (!this.generateBtn) return;
      if (this.mode === 'describe') {
        this.updateReadinessDescribe();
        return;
      }
      var ready = !!this.imageDataURL && !!this.selectedStyle;
      this.generateBtn.disabled = !ready || this.serviceDown;

      var hint;
      if (this.serviceDown) hint = this.strings.hintOffline;
      else if (!this.imageDataURL) hint = this.strings.hintNoPhoto;
      else if (!this.selectedStyle) hint = this.strings.hintNoStyle;
      else if (this.imagegenOffline) {

        hint = this.strings.hintWarming || this.strings.hintOffline;
      } else hint = this.strings.hintReady;
      if (this.hintEl) this.hintEl.textContent = hint || '';
    }

    updateReadinessDescribe() {
      var s = this.strings;
      var check = validatePrompt(this.promptInput ? this.promptInput.value : '');
      var ready = check.ok && !!this.selectedStyle;
      this.generateBtn.disabled = !ready || this.serviceDown;

      var hint;
      if (this.serviceDown) hint = s.hintOffline;
      else if (!check.ok) {
        if (check.code === 'empty') hint = s.hintNoPrompt;
        else if (check.code === 'short') hint = s.hintPromptShort;
        else hint = this.promptMessage(check.code);
      } else if (!this.selectedStyle) hint = s.hintNoStyle;
      else if (this.imagegenOffline) hint = s.hintWarmingDescribe || s.hintWarming || s.hintOffline;
      else hint = s.hintReady;
      if (this.hintEl) this.hintEl.textContent = hint || '';
    }

    setMode(mode) {
      var next = mode === 'describe' && this.describeEnabled ? 'describe' : 'photo';
      if (next !== this.mode) {
        this.selectedStyleByMode[this.mode] = this.selectedStyle;
        this.mode = next;
        this.selectedStyle = this.selectedStyleByMode[next] || null;
      }
      this.modeTabs.forEach(function (tab) {
        var on = tab.dataset.aipsModeTab === next;
        tab.setAttribute('aria-selected', String(on));
        tab.setAttribute('tabindex', on ? '0' : '-1');
        tab.classList.toggle('is-active', on);
      });
      this.modePanels.forEach(function (panel) {
        panel.hidden = panel.dataset.aipsPanel !== next;
      });
      this.updatePrivacy();
      if (this._stylesRendered) {
        this.refreshStyleControls();
        this.applyStyleFilter();
        this.syncTileSelection();
      }
      this.updateReadiness();
    }

    updatePrivacy() {
      if (!this.privacyText) return;
      var s = this.strings;
      var text;
      if (this.mode === 'describe') {
        text = this.featureLocal ? s.privacyDescribe : s.privacyDescribeRemote;
      } else {
        text = this.featureLocal ? s.privacyLocal : this.privacyDefault;
      }
      text = text || this.privacyDefault;
      if (text && this.privacyText.textContent.trim() !== text) this.privacyText.textContent = text;
    }

    bindDescribe() {
      var self = this;

      if (this.modeList && this.modeTabs.length) {
        this.modeTabs.forEach(function (tab) {
          tab.addEventListener('click', function () { self.setMode(tab.dataset.aipsModeTab); });
        });
        this.modeList.addEventListener('keydown', function (e) {
          var idx = self.modeTabs.indexOf(document.activeElement);
          if (idx < 0) return;
          var n = self.modeTabs.length;
          var next;
          switch (e.key) {
            case 'ArrowRight': next = (idx + 1) % n; break;
            case 'ArrowLeft': next = (idx - 1 + n) % n; break;
            case 'Home': next = 0; break;
            case 'End': next = n - 1; break;
            default: return;
          }
          e.preventDefault();
          var tab = self.modeTabs[next];
          self.setMode(tab.dataset.aipsModeTab);
          tab.focus();
        });
      }

      if (this.promptInput) {
        this.promptInput.addEventListener('input', function () {
          self.updatePromptCount();

          var flagged = (self.promptError && !self.promptError.hidden) || self.promptInput.hasAttribute('aria-invalid');
          if (flagged && validatePrompt(self.promptInput.value).ok) {
            self.clearPromptError();
          }
          self.updateReadiness();
        });
        this.promptInput.addEventListener('blur', function () {

          var check = validatePrompt(self.promptInput.value);
          if (!check.ok && check.code !== 'empty') self.promptInput.setAttribute('aria-invalid', 'true');
          else if (check.ok) self.clearPromptError();
        });
        this.promptInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();

            if (self.serviceDown) {
              self.updateReadiness();
              return;
            }
            self.generate();
          }
        });
        this.updatePromptCount();
      }

      this.querySelectorAll('[data-aips-example]').forEach(function (chip) {
        chip.addEventListener('click', function () { self.insertExample(chip.textContent); });
      });

      this.aspectInputs.forEach(function (input) {
        if (input.checked && ASPECTS[input.value]) self.aspect = input.value;
        input.addEventListener('change', function () {
          if (input.checked && ASPECTS[input.value]) self.aspect = input.value;
        });
      });
    }

    insertExample(text) {
      if (!this.promptInput) return;
      var example = normalizePrompt(text);
      if (!example) return;
      var current = normalizePrompt(this.promptInput.value);
      var isExample = false;
      this.querySelectorAll('[data-aips-example]').forEach(function (c) {
        if (normalizePrompt(c.textContent) === current) isExample = true;
      });

      var value = !current || isExample ? example : current + ' ' + example;
      this.promptInput.value = value.slice(0, PROMPT_MAX);
      this.updatePromptCount();
      if (validatePrompt(this.promptInput.value).ok) this.clearPromptError();
      this.updateReadiness();
      try {
        this.promptInput.focus({ preventScroll: true });
        var end = this.promptInput.value.length;
        this.promptInput.setSelectionRange(end, end);
      } catch (err) {}
      this.announce(this.strings.describeExampleAdded);
    }

    updatePromptCount() {
      if (!this.promptInput) return;
      var n = this.promptInput.value.length;
      if (this.promptCount && this.strings.describeCount) {
        this.promptCount.textContent = interp(this.strings.describeCount, { n: String(n), max: String(PROMPT_MAX) });
      }
      var band = n >= PROMPT_MAX ? 2 : n >= Math.ceil(PROMPT_MAX * 0.8) ? 1 : 0;
      if (band > this._countBand) {
        if (band === 2) this.announce(interp(this.strings.describeCountFull, { max: String(PROMPT_MAX) }));
        else this.announce(interp(this.strings.describeCountNear, { left: String(PROMPT_MAX - n) }));
      }
      this._countBand = band;
    }

    promptMessage(code) {
      var s = this.strings;
      if (code === 'empty') return s.describeRequired;
      if (code === 'short') return s.describeTooShort;
      if (code === 'long') return s.describeTooLong;
      return s.describeInvalidChars;
    }

    showPromptError(check) {
      if (!this.promptError || !check || check.ok) return;
      var text = this.promptMessage(check.code) || '';
      this.promptError.textContent = text;
      this.promptError.hidden = !text;
      if (this.promptInput) this.promptInput.setAttribute('aria-invalid', 'true');
    }

    clearPromptError() {
      if (this.promptError) {
        this.promptError.textContent = '';
        this.promptError.hidden = true;
      }
      if (this.promptInput) this.promptInput.removeAttribute('aria-invalid');
    }

    focusJobInput() {
      var mode = this.jobMode === 'describe' && this.describeEnabled ? 'describe' : 'photo';
      this.hideStage();
      if (mode !== this.mode) this.setMode(mode);
      var target = mode === 'describe'
        ? this.promptInput
        : ((this.previewWrap && !this.previewWrap.hidden && this.replaceBtn) || this.browseBtn);
      if (!target) return;
      var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var card = target.closest('.aips-upload') || target;
      card.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
      try { target.focus({ preventScroll: true }); } catch (err) {}
    }

    hideStage(opts) {

      this.stopTicker();
      this.stopQuiz();
      if (!(opts && opts.keepLive)) this.liveHide();
      this.stageCards.forEach(function (card) {
        if (card) card.hidden = true;
      });
    }

    showStage(card, opts) {
      if (!card) return;
      this.hideStage({ keepLive: card === this.progressCard || card === this.revealCard });
      card.hidden = false;
      if (motionOn()) {
        window.gsap.fromTo(
          card,
          { opacity: 0, y: 16 },
          { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }
        );
      }
      if (opts && opts.focus === false) return;
      var focusable = card.querySelector('[data-aips-focus]');
      if (focusable) focusable.focus({ preventScroll: false });
    }

    showQuotaCard(data) {
      this.stopPolling();
      this.liveHide();
      this.releaseBusy();
      if (data) this.applyQuota(data);
      if (this.quotaTitle && this.strings.quotaTitle) {
        var q = data || this.quota || {};
        var limit = q.limit != null ? q.limit : 2;
        var monthly = q.period === 'month';
        this.quotaTitle.textContent = interp(
          monthly && this.strings.quotaTitleMonth ? this.strings.quotaTitleMonth : this.strings.quotaTitle,
          { limit: String(limit) }
        );
        var body = this.quotaCard && this.quotaCard.querySelector('[data-aips-quota-body]');
        if (body) {
          body.textContent = (monthly && this.strings.quotaBodyMonth)
            ? this.strings.quotaBodyMonth
            : (this.strings.quotaBody || body.textContent);
        }
      }
      this.showStage(this.quotaCard);
    }

    showOffline(passive) {
      this.stopPolling();
      this.liveHide();
      this.releaseBusy();
      this.showStage(this.offlineCard, passive ? { focus: false } : undefined);
    }

    showError(code) {
      this.stopPolling();
      this.liveHide();
      this.releaseBusy();
      var s = this.strings;
      var c = typeof code === 'string' ? code : '';
      var title = s.errorTitle;
      var body = s.errorBody;
      var label = s.retry;
      var action = 'retry';
      if (c === 'content_rejected' || c === 'prompt_rejected') {

        title = s.rejectedTitle || title;

        body = (this.jobMode === 'describe' ? s.rejectedBody : s.rejectedBodyPhoto || s.rejectedBody) || body;
        label = s.rejectedAction || label;
        action = 'edit';
      } else if (c === 'invalid_prompt') {
        body = s.promptServerInvalid || body;
        label = s.rejectedAction || label;
        action = 'edit';
      } else if (c === 'rate_limited') {
        body = s.rateLimitedBody || body;
      }
      if (this.errorTitleEl && title) this.errorTitleEl.textContent = title;
      if (this.errorMsg && body) this.errorMsg.textContent = body;
      if (this.errorRetry && label) this.errorRetry.textContent = label;
      this.errorAction = action;
      this.showStage(this.errorCard);
    }

    showLate() {
      this.stopPolling();
      this.liveHide();
      this.releaseBusy();
      this.showStage(this.lateCard ? this.lateCard : this.errorCard);
    }

    bindActions() {
      var self = this;
      if (this.generateBtn) this.generateBtn.addEventListener('click', function () { self.generate(); });
      if (this.stylesRetry) this.stylesRetry.addEventListener('click', function () {
        if (self.serviceDown) self.boot();
        else self.loadStyles();
      });
      if (this.offlineRetry) this.offlineRetry.addEventListener('click', function () { self.boot(); });
      if (this.errorRetry) this.errorRetry.addEventListener('click', function () {
        if (self.errorAction === 'edit') self.focusJobInput();
        else self.retryLast();
      });

      if (this.personalInput) {
        this.personalInput.addEventListener('input', function () {
          window.clearTimeout(self._personalTimer);
          self._personalTimer = window.setTimeout(function () { self.applyPersonalText(); }, 250);
        });
      }
      if (this.personalPos) {
        this.personalPos.addEventListener('change', function () {
          self.edit.pos = self.personalPos.value;
          self.refreshEdits();
        });
      }
      if (this.personalFont) {
        this.personalFont.addEventListener('change', function () {
          self.edit.font = self.personalFont.value;
          self.refreshEdits();
        });
      }
      if (this.personalColors) {
        this.personalColors.addEventListener('click', function (e) {
          var sw = e.target.closest('[data-aips-color]');
          if (!sw) return;
          self.edit.color = sw.getAttribute('data-aips-color');
          self.personalColors.querySelectorAll('[data-aips-color]').forEach(function (b) {
            b.classList.toggle('is-active', b === sw);
            b.setAttribute('aria-pressed', b === sw ? 'true' : 'false');
          });
          self.refreshEdits();
        });
      }
      var stepSize = function (delta) {
        self.edit.sizePct = Math.max(60, Math.min(180, self.edit.sizePct + delta));
        if (self.sizeVal) self.sizeVal.textContent = self.edit.sizePct + '%';
        self.refreshEdits();
      };
      if (this.sizeMinus) this.sizeMinus.addEventListener('click', function () { stepSize(-10); });
      if (this.sizePlus) this.sizePlus.addEventListener('click', function () { stepSize(10); });
      if (this.adjWrap) {
        this.adjWrap.addEventListener('input', function (e) {
          var r = e.target.closest('[data-aips-adj]');
          if (!r) return;
          self.edit[r.getAttribute('data-aips-adj')] = parseInt(r.value, 10) || 100;

          if (self.flatImg) self.flatImg.style.filter = self.cssFilter();
          self.refreshEdits();
        });
      }
      if (this.adjReset) {
        this.adjReset.addEventListener('click', function () {
          self.edit.bright = 100; self.edit.contrast = 100; self.edit.sat = 100;
          if (self.adjWrap) self.adjWrap.querySelectorAll('[data-aips-adj]').forEach(function (r) { r.value = 100; });
          if (self.flatImg) self.flatImg.style.filter = self.cssFilter();
          self.refreshEdits();
        });
      }
      this.viewBtns.forEach(function (b) {
        b.addEventListener('click', function () { self.setResultView(b.getAttribute('data-aips-view')); });
      });
      this.bindLoupe();
      if (this.galleryListEl) {
        this.galleryListEl.addEventListener('click', function (e) {
          var tile = e.target.closest('[data-aips-gallery-pick]');
          if (!tile) return;
          self.stageArtwork(parseInt(tile.getAttribute('data-aips-gallery-pick'), 10) || 0);
        });
      }
      if (this.printBtn) this.printBtn.addEventListener('click', function () { self.printOnCanvas(); });
      if (this.anotherBtn) this.anotherBtn.addEventListener('click', function () { self.tryAnotherStyle(); });
      if (this.downloadLink) this.downloadLink.addEventListener('click', function (e) {

        if (!self.resultDataURL) e.preventDefault();
      });
    }

    releaseBusy() {
      this.busy = false;
      if (this.generateBtn) {
        this.generateBtn.classList.remove('btn--loading');
        this.generateBtn.removeAttribute('aria-disabled');
      }
      this.updateReadiness();
    }

    generate() {
      if (this.busy) return;

      if (this.mode === 'describe') {
        var check = validatePrompt(this.promptInput ? this.promptInput.value : '');
        if (!check.ok) {

          this.showPromptError(check);
          this.updateReadiness();
          return;
        }
        if (!this.selectedStyle) {
          this.updateReadiness();
          return;
        }

        this.jobMode = 'describe';
        this.jobPrompt = check.text;
        this.jobAspect = ASPECTS[this.aspect] ? this.aspect : 'landscape';
        this.jobImageDataURL = null;
        this.jobStyle = this.selectedStyle;
        this.submitJob();
        return;
      }

      if (!this.imageDataURL || !this.selectedStyle) {
        this.updateReadiness();
        return;
      }

      this.jobMode = 'photo';
      this.jobPrompt = '';
      this.jobAspect = '';
      this.jobImageDataURL = this.imageDataURL;
      this.jobStyle = this.selectedStyle;
      this.submitJob();
    }

    retryLast() {
      if (this.busy) return;
      if (!this.describeEnabled || !this.jobMode || !this.jobStyle) {
        this.generate();
        return;
      }
      var hasInput = this.jobMode === 'describe' ? !!this.jobPrompt : !!this.jobImageDataURL;
      if (!hasInput) {
        this.generate();
        return;
      }
      this.submitJob();
    }

    submitJob() {
      var self = this;

      this.liveHide();
      this.busy = true;
      this.generateBtn.classList.add('btn--loading');
      this.generateBtn.setAttribute('aria-disabled', 'true');

      this.loadQuota()
        .then(function (q) {
          if (self.isOutOfQuota(q)) {
            self.showQuotaCard(q);
            return null;
          }
          return self.jobMode === 'describe' ? self.postDescribe() : self.postGenerate();
        })
        .then(function (job) {
          if (job) self.startPolling(job);
        })
        .catch(function (err) {
          if (err && err.handled) return;
          self.showError(err && err.code);
        });
    }

    postGenerate() {
      var self = this;

      var payload = {
        image: this.jobImageDataURL || this.imageDataURL,
        style_id: (this.jobStyle || this.selectedStyle).id
      };

      if (this.customer) payload.customer = { id: this.customer.id };

      return this.authHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }).then(function (headers) {
        return fetchWithTimeout(self.api('/api/portrait/generate'), {
          method: 'POST',
          timeout: GENERATE_TIMEOUT,
          headers: headers,
          body: JSON.stringify(payload)
        });
      }).then(function (res) {
        return self.handleSubmitResponse(res);
      });
    }

    postDescribe() {
      var self = this;
      var style = this.jobStyle || this.autoStyle;
      var payload = {
        prompt: this.jobPrompt,
        style_id: String((style && style.id) || AUTO_STYLE_ID),
        aspect: ASPECTS[this.jobAspect] ? this.jobAspect : 'landscape'
      };
      if (this.customer) payload.customer = { id: this.customer.id };

      return this.authHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }).then(function (headers) {
        return fetchWithTimeout(self.api('/api/portrait/describe'), {
          method: 'POST',
          timeout: GENERATE_TIMEOUT,
          headers: headers,
          body: JSON.stringify(payload)
        });
      }).then(function (res) {
        return self.handleSubmitResponse(res);
      });
    }

    handleSubmitResponse(res) {
      var self = this;
      if (res.status === 402) {
        return safeJson(res).then(function (data) {
          self.showQuotaCard(data);
          var e = new Error('quota');
          e.handled = true;
          throw e;
        });
      }
      if (res.status === 503) {
        return safeJson(res).then(function (data) {

          if (data && data.error === 'busy') throw new Error('busy');
          self.imagegenOffline = true;
          self.showOffline();
          self.updateReadiness();
          var e = new Error('offline');
          e.handled = true;
          throw e;
        });
      }
      if (res.status === 400 || res.status === 403 || res.status === 429) {
        return safeJson(res).then(function (data) {
          var code = data && typeof data.error === 'string' ? data.error : '';
          if (res.status === 429) code = 'rate_limited';
          else if (res.status === 403) code = 'banned';
          var e = new Error(code || 'HTTP ' + res.status);
          e.code = code;
          throw e;
        });
      }
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return res.json();
    }

    startPolling(job) {
      var self = this;
      var jobId = job && job.job_id;
      if (!jobId) {
        this.showError();
        return;
      }
      this.stopPolling();
      this.jobId = jobId;
      this.pollFails = 0;
      this.pollStart = Date.now();
      this.progressHint = 0;
      this.etaS = job && typeof job.etaSeconds === 'number' && job.etaSeconds > 0 ? job.etaSeconds : DEFAULT_ETA_S;

      if (this.jobMode === 'describe') {

        if (this.progressImg) {
          this.progressImg.hidden = true;
          this.progressImg.removeAttribute('src');
        }
        if (this.progressMedia) {
          this.progressMedia.classList.add('aips-progress-media--blank');
          this.progressMedia.setAttribute('data-aspect', ASPECTS[this.jobAspect] ? this.jobAspect : 'landscape');
        }
        if (this.promptQuote) this.promptQuote.textContent = this.jobPrompt || '';
        if (this.promptQuoteWrap) this.promptQuoteWrap.hidden = !this.jobPrompt;
      } else {
        if (this.progressMedia) {
          this.progressMedia.classList.remove('aips-progress-media--blank');
          this.progressMedia.removeAttribute('data-aspect');
        }
        if (this.progressImg) this.progressImg.hidden = false;
        if (this.promptQuoteWrap) this.promptQuoteWrap.hidden = true;
        var theaterSrc = this.jobImageDataURL || this.imageDataURL;
        if (this.progressImg && theaterSrc) this.progressImg.src = theaterSrc;
      }

      this.liveStart(jobId);
      this.updateQueue(job && job.queue_position);

      var watchLive = this.liveRendered();
      this.showStage(this.progressCard, watchLive ? { focus: false } : undefined);
      if (watchLive) this.liveBringIntoView();
      this.setStage((job && job.stage) || 'queued');
      this.startTicker();
      this.startQuiz();
      this.announce(this.strings.statusGenerating);

      this.pollTimer = setTimeout(function () { self.poll(); }, POLL_MS);
    }

    stopPolling() {
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
      this.jobId = null;
    }

    poll() {
      var self = this;
      var jobId = this.jobId;
      if (!jobId) return;

      if (Date.now() - this.pollStart > POLL_DEADLINE_MS) {

        this.showLate();
        return;
      }

      fetchJSON(this.api('/api/portrait/job/' + encodeURIComponent(jobId)), { timeout: MISC_TIMEOUT })
        .then(function (job) {
          if (self.jobId !== jobId) return;
          self.pollFails = 0;
          self.syncWait(job);

          switch (job && job.status) {
            case 'queued':
              self.setStage((job && job.stage) || 'queued');
              self.updateQueue(job.queue_position);
              break;
            case 'running':
              self.setStage((job && job.stage) || 'painting');
              self.updateQueue(0);
              if (typeof job.progress === 'number') self.progressHint = clamp(job.progress, 0, 1);
              break;
            case 'done':
              self.stopPolling();
              if (job.image) self.onDone(job.image);
              else if (isLateJob(job)) self.showLate();
              else self.showError();
              return;
            case 'error':
              if (isLateJob(job)) self.showLate();
              else self.showError(job && job.error);
              return;
            default:
              if (isLateJob(job)) {
                self.showLate();
                return;
              }
              break;
          }
          self.pollTimer = setTimeout(function () { self.poll(); }, POLL_MS);
        })
        .catch(function (err) {
          if (self.jobId !== jobId) return;

          if (err && err.status === 404) {
            self.showLate();
            return;
          }
          self.pollFails++;
          if (self.pollFails >= MAX_POLL_FAILS) {
            self.showError();
            return;
          }
          self.pollTimer = setTimeout(function () { self.poll(); }, POLL_MS);
        });
    }

    updateQueue(position) {
      var n = typeof position === 'number' ? position : 0;
      this.liveQueue(n);
      if (!this.queueEl) return;
      if (n > 0) {
        this.queueEl.textContent = interp(this.strings.queuePosition, { n: String(n) });
        this.queueEl.hidden = false;
      } else {
        this.queueEl.hidden = true;
      }
    }

    setStage(stage) {
      var mapped = STAGE_FLOORS[stage] != null ? stage : 'painting';
      this.stage = mapped;
      this.liveStage(mapped);
      if (!this.stageLine) return;
      var copies = this.stageCopies;
      var copy = (this.jobMode === 'describe' && copies[mapped + '_describe']) || copies[mapped] || copies.painting || '';
      if (!copy || this.stageLine.textContent === copy) return;
      var line = this.stageLine;
      if (!line.textContent) {
        line.textContent = copy;
        return;
      }
      line.classList.add('is-swapping');
      setTimeout(function () {
        line.textContent = copy;
        line.classList.remove('is-swapping');
      }, 220);
    }

    syncWait(job) {
      if (!job) return;
      if (typeof job.elapsedSeconds === 'number' && job.elapsedSeconds >= 0) {
        this.elapsedS = job.elapsedSeconds;
        this.tickBase = Date.now();
      }
      if (typeof job.etaSeconds === 'number' && job.etaSeconds > 0) {
        var polledElapsed =
          typeof job.elapsedSeconds === 'number' && job.elapsedSeconds >= 0 ? job.elapsedSeconds : 0;
        this.etaS = polledElapsed + job.etaSeconds;
      }
    }

    startTicker() {
      var self = this;
      this.stopTicker();
      this.elapsedS = 0;
      this.tickBase = Date.now();
      this.renderWait();
      this.tickTimer = setInterval(function () { self.renderWait(); }, TICK_MS);
    }

    stopTicker() {
      if (this.tickTimer) {
        clearInterval(this.tickTimer);
        this.tickTimer = null;
      }
    }

    renderWait() {
      var elapsed = this.elapsedS + (Date.now() - this.tickBase) / 1000;
      var elapsedText = interp(this.strings.waitElapsed, { time: formatClock(elapsed) });

      if (this.elapsedEl) this.elapsedEl.textContent = elapsedText;

      var pct = this.waitPercent(elapsed);
      this.liveProgress(pct, elapsedText);

      if (!this.barEl) return;
      this.barEl.parentElement.classList.remove('is-indeterminate');
      this.barEl.style.width = pct + '%';
    }

    waitPercent(elapsed) {
      var eta = Math.max(30, this.etaS || DEFAULT_ETA_S);
      var approach = 96 * (1 - Math.exp(-1.6 * (elapsed / eta)));
      var pct = Math.max(STAGE_FLOORS[this.stage] || 0, approach, this.progressHint * 96);
      return clamp(Math.round(pct), 2, 96);
    }

    bindLivePanel() {
      var self = this;
      if (!this.livePanel) return;
      if (this.liveSeeBtn) this.liveSeeBtn.addEventListener('click', function () { self.liveSeeArtwork(); });
      if (window.matchMedia) {

        this.liveMq = window.matchMedia(LIVE_MQ);
        var onChange = function () { self.liveSyncEngine(); };
        if (this.liveMq.addEventListener) this.liveMq.addEventListener('change', onChange);
        else if (this.liveMq.addListener) this.liveMq.addListener(onChange);
      }
    }

    liveStart(jobId) {
      if (!this.livePanel) return;
      var wasShown = !this.livePanel.hidden;
      this.liveHide();

      if (wasShown) void this.livePanel.offsetHeight;
      var live = this.live;
      live.active = true;
      live.jobId = jobId;
      live.mode = this.jobMode === 'describe' ? 'describe' : 'photo';
      live.prompt = live.mode === 'describe' ? this.jobPrompt : '';
      live.photo = live.mode === 'describe' ? '' : (this.jobImageDataURL || this.imageDataURL || '');
      live.stage = 'queued';
      live.stepKey = '';
      live.swept = false;
      if (live.mode === 'describe') {
        live.seed = hashString(normalizePrompt(live.prompt).toLowerCase());
        live.ar = LIVE_ASPECTS[this.jobAspect] || LIVE_ASPECTS.landscape;
      } else {
        live.seed = hashString(String(jobId));
        live.ar = this.liveGuessPhotoAspect(live.photo);
      }
      this.liveSetAspect(live.ar);
      this.liveRenderCaption();
      this.liveRenderSteps();
      this.liveRenderPct(0);
      if (this.liveElapsed) this.liveElapsed.textContent = '';
      this.livePanel.hidden = false;
      this.liveSyncEngine();
    }

    liveHide() {
      var live = this.live;
      if (!live) return;
      live.token++;
      live.active = false;
      live.done = false;
      live.jobId = null;
      live.prompt = '';
      live.photo = '';
      live.source = null;
      live.sourcePending = false;
      live.pct = -1;
      live.doneImage = '';
      live.doneShown = '';
      live.doneFit = false;
      this.liveStopEngine();
      var panel = this.livePanel;
      if (!panel) return;
      panel.hidden = true;
      panel.classList.remove('is-done');
      if (this.liveFrame) {
        this.liveFrame.classList.remove('is-sweeping', 'is-static');
        this.liveFrame.style.removeProperty('--aips-live-edge-r');
        this.liveFrame.style.removeProperty('--aips-live-edge-b');
      }
      var img = this.liveResult;
      if (img) {
        img.onload = null;
        img.hidden = true;
        img.classList.remove('is-shown');
        img.removeAttribute('src');
      }
      if (this.liveDoneEl) this.liveDoneEl.hidden = true;
      if (this.liveQueueEl) this.liveQueueEl.hidden = true;
      if (this.liveBar) this.liveBar.style.width = '0%';
      if (this.livePct) this.livePct.textContent = '';
    }

    liveStopEngine() {
      if (!this.livePainter) return;
      this.livePainter.destroy();
      this.livePainter = null;
    }

    liveRendered() {
      var panel = this.livePanel;
      if (!panel || panel.hidden) return false;
      if (this.liveMq && !this.liveMq.matches) return false;
      return panel.getClientRects().length > 0;
    }

    liveBringIntoView() {
      var panel = this.livePanel;
      var rect = panel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var clear = parseFloat(window.getComputedStyle(panel).scrollMarginTop) || 0;
      if (rect.top < clear || rect.bottom > vh) {
        panel.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }
      var focusable = this.progressCard && this.progressCard.querySelector('[data-aips-focus]');
      if (focusable) {
        try { focusable.focus({ preventScroll: true }); } catch (err) {}
      }
    }

    liveWatching() {
      var live = this.live;
      if (!live || !live.active || live.done || !this.liveRendered()) return false;
      var rect = (this.liveFrame || this.livePanel).getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return rect.bottom > 0 && rect.top < vh;
    }

    liveSyncEngine() {
      var live = this.live;
      if (live && live.done) this.liveShowDoneImage();
      if (!live || !live.active || live.done || !this.liveCanvas || !this.liveRendered()) {
        this.liveStopEngine();
        return;
      }
      if (this.livePainter) return;
      var ctx = null;
      try {
        ctx = this.liveCanvas.getContext('2d');
      } catch (err) {
        ctx = null;
      }
      if (!ctx) {

        if (this.liveFrame) this.liveFrame.classList.add('is-static');
        return;
      }
      this.livePainter = new LivePainter(this.liveCanvas, ctx, {
        rand: seededRandom(live.seed ^ 0x5bd1e995),
        reduced: reducedMotion(),
        observe: this.liveFrame || this.liveCanvas
      });
      this.livePainter.setPhase(livePhase(live.stage), this.liveBrushProgress());
      this.liveEnsureSource();
    }

    liveEnsureSource() {
      var self = this;
      var live = this.live;
      if (live.source) {
        this.liveSourceReady();
        return;
      }
      if (live.sourcePending) return;
      if (live.mode === 'describe' || !live.photo) {
        live.source = liveDescribeSource(live.prompt, live.ar, seededRandom(live.seed));
        this.liveSourceReady();
        return;
      }
      var token = live.token;
      var img = new Image();
      live.sourcePending = true;
      img.onload = function () {
        var cur = self.live;
        if (cur.token !== token || !cur.active || cur.done) return;
        cur.sourcePending = false;
        if (img.naturalWidth && img.naturalHeight) {
          cur.ar = clamp(img.naturalWidth / img.naturalHeight, LIVE_AR_MIN, LIVE_AR_MAX);
          self.liveSetAspect(cur.ar);
          cur.source = livePhotoSource(img, cur.ar, seededRandom(cur.seed));
        }
        if (!cur.source) cur.source = liveDescribeSource('', cur.ar, seededRandom(cur.seed));
        self.liveSourceReady();
      };
      img.onerror = function () {
        var cur = self.live;
        if (cur.token !== token || !cur.active || cur.done) return;
        cur.sourcePending = false;
        cur.source = liveDescribeSource('', cur.ar, seededRandom(cur.seed));
        self.liveSourceReady();
      };
      img.src = live.photo;
    }

    liveSourceReady() {
      if (this.livePainter && this.live.source) this.livePainter.setSource(this.live.source);
      this.liveApplyEdges();
    }

    liveApplyEdges() {
      var frame = this.liveFrame;
      var live = this.live;
      if (!frame || live.done) return;
      if (live.source && livePhase(live.stage) !== 'sketch') {
        frame.style.setProperty('--aips-live-edge-r', live.source.edgeRight);
        frame.style.setProperty('--aips-live-edge-b', live.source.edgeBottom);
      } else {
        frame.style.removeProperty('--aips-live-edge-r');
        frame.style.removeProperty('--aips-live-edge-b');
      }
    }

    liveGuessPhotoAspect(url) {
      var img = this.previewImg;
      if (url && img && img.naturalWidth && img.naturalHeight && img.getAttribute('src') === url) {
        return clamp(img.naturalWidth / img.naturalHeight, LIVE_AR_MIN, LIVE_AR_MAX);
      }
      return 4 / 5;
    }

    liveSetAspect(ar) {
      if (this.liveFrame) this.liveFrame.style.setProperty('--aips-live-ar', ar.toFixed(4));
    }

    liveRenderCaption() {
      var el = this.liveCaption;
      if (!el) return;
      el.textContent = '';
      if (this.live.mode === 'describe') {
        var idea = normalizePrompt(this.live.prompt);
        if (idea) {
          var q = document.createElement('q');
          q.textContent = idea;
          el.appendChild(q);
        }
      } else {
        var style = this.jobStyle || this.selectedStyle;
        var name = '';
        if (style) name = style.auto ? (this.strings.styleAuto || style.displayTitle || '') : (style.displayTitle || style.title || '');
        if (name && this.liveStrings.captionPhoto) el.textContent = interp(this.liveStrings.captionPhoto, { style: name });
      }
      el.hidden = !el.textContent;
    }

    liveRenderSteps() {
      var live = this.live;
      var s = this.liveStrings;
      var copy = s.steps || {};
      var idx = live.done ? LIVE_STEPS.length : Math.max(0, LIVE_STEPS.indexOf(live.stage));
      var waiting = !live.done && live.stage === 'queued';
      var key = idx + '|' + waiting + '|' + live.mode;
      if (key === live.stepKey) return;
      live.stepKey = key;
      var first = waiting ? copy.queued : (live.mode === 'describe' ? copy.checking_describe : copy.checking);
      this.liveSteps.forEach(function (li, i) {
        var state = i < idx ? 'done' : (i === idx ? 'active' : 'todo');
        li.classList.toggle('is-done', state === 'done');
        li.classList.toggle('is-active', state === 'active');
        if (state === 'active') li.setAttribute('aria-current', 'step');
        else li.removeAttribute('aria-current');
        var stateEl = li.querySelector('[data-aips-live-step-state]');
        if (stateEl) stateEl.textContent = state === 'done' ? (s.stateDone || '') : (state === 'active' ? (s.stateActive || '') : '');
        if (i === 0 && first) {
          var label = li.querySelector('[data-aips-live-step-label]');
          if (label) label.textContent = first;
        }
      });
    }

    liveRenderPct(pct) {
      var live = this.live;
      if (pct === live.pct) return;
      live.pct = pct;
      if (this.livePct) this.livePct.textContent = this.liveFormatPct(pct);
      if (this.liveBar) this.liveBar.style.width = pct + '%';
    }

    liveFormatPct(pct) {
      if (this._livePctFmt === undefined) {
        try {
          this._livePctFmt = new Intl.NumberFormat(html.lang || undefined, { style: 'percent', maximumFractionDigits: 0 });
        } catch (err) {
          this._livePctFmt = null;
        }
      }
      return this._livePctFmt ? this._livePctFmt.format(pct / 100) : pct + '%';
    }

    liveBrushProgress() {
      var from = STAGE_FLOORS.painting;
      var to = STAGE_FLOORS.finishing;
      return clamp((this.live.pct - from) / (to - from), 0, 1);
    }

    liveOwnsJob() {
      var live = this.live;
      return !!(live && live.active && !live.done && live.jobId && live.jobId === this.jobId);
    }

    liveStage(stage) {
      var live = this.live;
      if (!this.liveOwnsJob()) return;
      if ((LIVE_STAGE_ORDER[stage] || 0) < (LIVE_STAGE_ORDER[live.stage] || 0)) return;
      live.stage = stage;
      this.liveRenderSteps();
      var floor = STAGE_FLOORS[stage] || 0;
      if (floor > live.pct) this.liveRenderPct(floor);
      if (stage === 'finishing' && !live.swept) {

        live.swept = true;
        if (this.liveFrame && !reducedMotion()) this.liveFrame.classList.add('is-sweeping');
      }
      if (this.livePainter) this.livePainter.setPhase(livePhase(stage), this.liveBrushProgress());
      this.liveApplyEdges();
    }

    liveProgress(pct, elapsedText) {
      var live = this.live;
      if (!this.liveOwnsJob()) return;
      this.liveRenderPct(Math.max(live.pct, clamp(Math.round(pct), 0, 99)));
      if (this.liveElapsed && elapsedText) this.liveElapsed.textContent = elapsedText;
      if (this.livePainter) this.livePainter.setPhase(livePhase(live.stage), this.liveBrushProgress());
    }

    liveQueue(n) {
      var el = this.liveQueueEl;
      if (!el || !this.liveOwnsJob()) return;
      var show = n > 0 && !!this.liveStrings.queue;
      if (show) el.textContent = interp(this.liveStrings.queue, { n: String(n) });
      el.hidden = !show;
    }

    liveDone(image) {
      var live = this.live;
      if (!live || !live.active || live.done || !this.livePanel) return;
      live.done = true;
      live.sourcePending = false;
      this.liveStopEngine();
      this.liveRenderPct(100);
      this.liveRenderSteps();
      if (this.liveQueueEl) this.liveQueueEl.hidden = true;
      if (this.liveFrame) this.liveFrame.classList.remove('is-sweeping');
      this.livePanel.classList.add('is-done');
      if (this.liveDoneEl) this.liveDoneEl.hidden = false;
      live.doneImage = image || '';
      this.liveShowDoneImage();
    }

    liveShowDoneImage() {
      var live = this.live;
      var img = this.liveResult;
      if (!img || !live || !live.done || !live.doneImage || live.doneShown === live.doneImage) return;
      if (!this.liveRendered()) return;
      var self = this;
      var token = live.token;
      var image = live.doneImage;
      var fade = !img.classList.contains('is-shown');
      var fit = !!live.doneFit;
      live.doneShown = image;
      var show = function () {
        if (self.live.token !== token || self.live.doneShown !== image) return;
        if (fit && img.naturalWidth && img.naturalHeight) {
          self.liveSetAspect(clamp(img.naturalWidth / img.naturalHeight, LIVE_AR_MIN, LIVE_AR_MAX));
        }
        self.liveEdgesFromImage(img);
        if (!fade) return;
        img.hidden = false;
        void img.offsetWidth;
        img.classList.add('is-shown');
      };
      img.onload = null;
      img.src = image;
      if (typeof img.decode === 'function') {
        img.decode().then(show, function () {  });
      } else {
        img.onload = function () {
          img.onload = null;
          show();
        };
      }
    }

    liveStageArtwork(item) {
      var live = this.live;
      if (!live || !live.done || !item || !item.after) return;
      live.mode = item.mode === 'describe' ? 'describe' : 'photo';
      live.prompt = live.mode === 'describe' ? (item.prompt || '') : '';
      live.stepKey = '';
      this.liveRenderCaption();
      this.liveRenderSteps();
      live.doneFit = true;
      live.doneImage = item.after;
      this.liveShowDoneImage();
    }

    liveEdgesFromImage(img) {
      var frame = this.liveFrame;
      if (!frame) return;
      try {
        var c = document.createElement('canvas');
        c.width = 8;
        c.height = 8;
        var ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0, 8, 8);
        var px = ctx.getImageData(0, 0, 8, 8).data;
        frame.style.setProperty('--aips-live-edge-r', liveEdgeColor(px, 8, 7, 0, 0, 1, 8));
        frame.style.setProperty('--aips-live-edge-b', liveEdgeColor(px, 8, 0, 7, 1, 0, 8));
      } catch (err) {

      }
    }

    liveSeeArtwork() {
      var card = this.revealCard;
      if (!card || card.hidden) {
        this.liveHide();
        return;
      }
      card.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
      var focusable = card.querySelector('[data-aips-focus]');
      if (focusable) {
        try { focusable.focus({ preventScroll: true }); } catch (err) {}
      }
    }

    startQuiz() {
      var self = this;
      if (!this.quizWrap || !this.quizOptions || !this.quizImg) return;
      this.quizActive = true;
      this.quizStreak = 0;
      this.quizLastId = null;
      this.renderQuizStreak();

      if (this.quizPool && this.quizPool.length >= 4) {
        this.quizWrap.hidden = false;
        this.quizRound();
        return;
      }

      this.loadQuizPool()
        .then(function (pool) {
          if (!self.quizActive) return;
          if (!pool || pool.length < 4) return;
          self.quizPool = pool;
          self.quizWrap.hidden = false;
          self.quizRound();
        })
        .catch(function () {

        });
    }

    stopQuiz() {
      this.quizActive = false;
      this.quizCorrectItem = null;
      if (this.quizTimer) {
        clearTimeout(this.quizTimer);
        this.quizTimer = null;
      }
      if (this.quizWrap) this.quizWrap.hidden = true;
      if (this.quizOptions) this.quizOptions.textContent = '';
      if (this.quizFeedback) {
        this.quizFeedback.hidden = true;
        this.quizFeedback.textContent = '';
      }
    }

    loadQuizPool() {
      return fetchJSON('/collections.json?limit=250', { timeout: MISC_TIMEOUT })
        .then(function (data) {
          var all = data && Array.isArray(data.collections) ? data.collections : [];
          var candidates = all.filter(function (c) {
            if (!c || !c.handle || QUIZ_EXCLUDED_HANDLES[c.handle]) return false;
            return typeof c.products_count !== 'number' || c.products_count >= 6;
          });
          if (!candidates.length) return [];
          var picks = shuffle(candidates.slice()).slice(0, 2);
          return Promise.all(
            picks.map(function (c) {
              return fetchJSON('/collections/' + encodeURIComponent(c.handle) + '/products.json?limit=50', { timeout: MISC_TIMEOUT })
                .then(function (d) { return d && Array.isArray(d.products) ? d.products : []; })
                .catch(function () { return []; });
            })
          );
        })
        .then(function (lists) {
          var seen = {};
          var pool = [];
          (lists || []).forEach(function (products) {
            products.forEach(function (p) {
              if (!p || p.id == null || seen[p.id]) return;
              var img = p.images && p.images[0] && p.images[0].src;
              if (!img || !p.title || !p.handle) return;
              seen[p.id] = true;
              pool.push({ id: p.id, title: p.title, handle: p.handle, image: img });
            });
          });
          return pool;
        });
    }

    quizRound() {
      if (!this.quizActive || !this.quizPool || this.quizPool.length < 4) return;
      var pool = this.quizPool;

      var correct = pool[Math.floor(Math.random() * pool.length)];
      var guard = 0;
      while (pool.length > 1 && correct.id === this.quizLastId && guard++ < 20) {
        correct = pool[Math.floor(Math.random() * pool.length)];
      }
      this.quizLastId = correct.id;
      this.quizCorrectItem = correct;

      var decoys = [];
      var seenTitles = {};
      seenTitles[correct.title] = true;
      var candidates = shuffle(pool.slice());
      for (var i = 0; i < candidates.length && decoys.length < 3; i++) {
        var p = candidates[i];
        if (p.id === correct.id || seenTitles[p.title]) continue;
        seenTitles[p.title] = true;
        decoys.push(p);
      }
      if (decoys.length < 3) {
        this.stopQuiz();
        return;
      }

      if (this.quizImg) this.quizImg.src = sizedImage(correct.image, 600);
      if (this.quizFeedback) {
        this.quizFeedback.hidden = true;
        this.quizFeedback.textContent = '';
      }

      var self = this;
      this.quizOptions.textContent = '';
      shuffle([correct].concat(decoys)).forEach(function (item) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'aips-quiz-option';
        btn.textContent = item.title;
        btn.addEventListener('click', function () { self.answerQuiz(item, btn); });
        self.quizOptions.appendChild(btn);
      });
    }

    answerQuiz(item, btn) {
      if (!this.quizActive || !this.quizCorrectItem) return;
      var correct = this.quizCorrectItem;
      this.quizCorrectItem = null;
      var right = item.id === correct.id;
      var self = this;

      this.quizOptions.querySelectorAll('.aips-quiz-option').forEach(function (b) {
        b.disabled = true;
        var isCorrectBtn = b.textContent === correct.title;
        if (isCorrectBtn || b === btn) {
          var mark = document.createElement('span');
          mark.className = 'aips-quiz-mark';
          mark.setAttribute('aria-hidden', 'true');
          mark.textContent = isCorrectBtn ? '✓' : '✕';
          b.insertBefore(mark, b.firstChild);
          b.classList.add(isCorrectBtn ? 'is-right' : 'is-wrong');
        }
      });

      this.quizStreak = right ? this.quizStreak + 1 : 0;
      this.renderQuizStreak();

      if (this.quizFeedback) {
        this.quizFeedback.textContent = interp(right ? this.strings.quizCorrect : this.strings.quizWrong, { title: correct.title });
        if (right && this.strings.quizView) {
          this.quizFeedback.appendChild(document.createTextNode(' '));
          var link = document.createElement('a');
          link.className = 'link-underline aips-quiz-view';
          link.href = '/products/' + correct.handle;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = this.strings.quizView + ' →';
          this.quizFeedback.appendChild(link);
        }
        this.quizFeedback.hidden = false;

        this.quizFeedback.setAttribute('tabindex', '-1');
        try { this.quizFeedback.focus({ preventScroll: true }); } catch (err) {}
      }

      var advance = function () {
        if (!self.quizActive) return;
        var fb = self.quizFeedback;
        var active = document.activeElement;
        var reading = false;
        if (fb && !fb.hidden) {
          var hovered = false;
          try { hovered = fb.matches(':hover'); } catch (err) {}
          reading = hovered || (active && active !== fb && fb.contains(active));
        }
        if (reading) {
          self.quizTimer = setTimeout(advance, QUIZ_ADVANCE_MS);
          return;
        }
        var refocus = !!(fb && active && (active === fb || fb.contains(active)));
        self.quizRound();
        if (refocus && self.quizActive && self.quizOptions) {
          var first = self.quizOptions.querySelector('.aips-quiz-option');
          if (first) { try { first.focus({ preventScroll: true }); } catch (err) {} }
        }
      };
      this.quizTimer = setTimeout(advance, QUIZ_ADVANCE_MS);
    }

    renderQuizStreak() {
      if (!this.quizStreakEl) return;
      if (this.quizStreak > 0 && this.strings.quizStreak) {
        this.quizStreakEl.textContent = interp(this.strings.quizStreak, { n: String(this.quizStreak) });
        this.quizStreakEl.hidden = false;
      } else {
        this.quizStreakEl.hidden = true;
      }
    }

    onDone(image) {
      if (!image) {
        this.showError();
        return;
      }
      this.releaseBusy();

      var jobStyle = this.jobStyle || this.selectedStyle;
      var described = this.jobMode === 'describe';
      this.applyRevealLabel(described ? 'describe' : 'photo', this.jobPrompt);
      this.stageEditsForNewArtwork(image);
      this.mountResultViewer(this.resultDataURL);
      this.applyDownloadLink(this.resultDataURL, jobStyle);

      this.sessionArtworks.unshift({
        after: image,
        before: described ? '' : (this.jobImageDataURL || this.imageDataURL || ''),
        style: jobStyle || null,

        title: (jobStyle && !jobStyle.auto && (jobStyle.displayTitle || jobStyle.title)) || '',
        mode: described ? 'describe' : 'photo',
        prompt: described ? this.jobPrompt : '',
        aspect: described ? this.jobAspect : ''
      });
      if (this.sessionArtworks.length > SESSION_ARTWORK_MAX) {
        this.sessionArtworks.length = SESSION_ARTWORK_MAX;
      }
      this.stagedArtwork = 0;
      this.renderGallery();

      var watching = this.liveWatching();
      this.showStage(this.revealCard, watching ? { focus: false } : undefined);
      this.liveDone(image);
      if (watching && this.liveSeeBtn) {
        try { this.liveSeeBtn.focus({ preventScroll: true }); } catch (err) {}
      }
      this.announce(this.strings.statusDone);

      if (motionOn() && this.canvas3dHost) {
        window.gsap.fromTo(
          this.canvas3dHost,
          { opacity: 0, scale: 0.985 },
          { opacity: 1, scale: 1, duration: 0.9, ease: 'expo.out', clearProps: 'opacity,transform' }
        );
      }

      this.loadQuota();
    }

    initCompare() {
      var self = this;
      if (!this.compareEl || !this.handleEl) return;

      this.setCut = function (pct) {
        pct = clamp(pct, 0, 100);
        self.cut = pct;
        self.compareEl.style.setProperty('--aips-cut', pct + '%');
        self.handleEl.setAttribute('aria-valuenow', String(Math.round(pct)));
      };

      var pctFromEvent = function (e) {
        var rect = self.compareEl.getBoundingClientRect();
        if (!rect.width) return self.cut;
        return ((e.clientX - rect.left) / rect.width) * 100;
      };

      var dragging = false;
      var pendingTouch = null;
      this.compareEl.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        if (e.pointerType === 'touch') {

          pendingTouch = { id: e.pointerId, x: e.clientX, y: e.clientY };
          return;
        }
        dragging = true;
        try { self.compareEl.setPointerCapture(e.pointerId); } catch (err) {}
        self.setCut(pctFromEvent(e));
        e.preventDefault();
      });
      this.compareEl.addEventListener('pointermove', function (e) {
        if (!dragging && pendingTouch && e.pointerId === pendingTouch.id) {
          var dx = Math.abs(e.clientX - pendingTouch.x);
          var dy = Math.abs(e.clientY - pendingTouch.y);
          if (dx > 6 && dx > dy) {
            dragging = true;
            pendingTouch = null;
            try { self.compareEl.setPointerCapture(e.pointerId); } catch (err) {}
          } else if (dy > 10 && dy > dx) {
            pendingTouch = null;
          }
        }
        if (dragging) self.setCut(pctFromEvent(e));
      });
      ['pointerup', 'pointercancel'].forEach(function (name) {
        self.compareEl.addEventListener(name, function () {
          dragging = false;
          pendingTouch = null;
        });
      });

      this.handleEl.addEventListener('keydown', function (e) {
        var step = 4;
        switch (e.key) {
          case 'ArrowLeft': self.setCut(self.cut - step); break;
          case 'ArrowRight': self.setCut(self.cut + step); break;
          case 'Home': self.setCut(0); break;
          case 'End': self.setCut(100); break;
          default: return;
        }
        e.preventDefault();
      });
    }

    maybeLoadFromHash() {
      var m = /^#ai-artwork=([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
        .exec(window.location.hash || '');
      if (!m) return;
      var id = m[1].toLowerCase();
      var self = this;

      this.authHeaders()
        .then(function (headers) {
          return fetchJSON(self.api('/api/portrait/history' + self.quotaQuery()), {
            timeout: MISC_TIMEOUT, headers: headers
          });
        })
        .then(function (data) {
          var items = (data && Array.isArray(data.items)) ? data.items : [];
          var item = null;
          for (var i = 0; i < items.length; i++) {
            if (items[i] && items[i].id === id) { item = items[i]; break; }
          }
          if (!item || !item.url) return null;

          return fetchWithTimeout(self.api(item.url), { timeout: 20000 })
            .then(function (res) { return res.ok ? res.blob() : null; })
            .then(function (blob) {
              if (!blob) return null;
              return new Promise(function (resolve) {
                var reader = new FileReader();
                reader.onload = function () { resolve(String(reader.result || '')); };
                reader.onerror = function () { resolve(null); };
                reader.readAsDataURL(blob);
              });
            })
            .then(function (dataURL) {
              if (!dataURL) return;
              document.dispatchEvent(
                new CustomEvent('airx:ai-portrait:ready', {
                  detail: {
                    dataURL: dataURL,
                    styleId: item.style_id || '',
                    styleTitle: item.style_title || ''
                  }
                })
              );

              if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
              }
            });
        })
        .catch(function () {

        });
    }

    maybeLoadCuratorArt() {
      var params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (err) {
        return false;
      }
      if (!params.has('cur')) return false;

      if (!document.querySelector('[data-cyo-root] input[data-cyo-file]')) return true;

      var self = this;
      var expired = this.strings.curatorLinkExpired ||
        'This painting link has expired — ask the curator to paint it again.';
      var link = parseCuratorLink(params);
      if (!link) {
        this.curatorNotice(expired, true);
        return true;
      }
      this.curatorNotice(this.strings.curatorLinkLoading || 'Bringing in your painting…', true);

      var url = this.api('/api/curator/art/' + encodeURIComponent(link.id) + '/print' +
        '?exp=' + encodeURIComponent(link.exp) + '&sig=' + encodeURIComponent(link.sig));
      fetchWithTimeout(url, { timeout: CURATOR_FETCH_TIMEOUT })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.blob();
        })
        .then(function (blob) {
          if (!blob || !blob.size || blob.size > MAX_UPLOAD_BYTES || !CURATOR_IMAGE_TYPE.test(blob.type || '')) {
            throw new Error('Not an artwork');
          }
          return blobToDataURL(blob);
        })
        .then(function (dataURL) {
          if (!dataURL || !/^data:image\/(png|jpeg|webp);base64,/i.test(dataURL)) throw new Error('Unreadable artwork');
          whenDocumentSettled(function () {
            self.curatorNotice('');
            document.dispatchEvent(
              new CustomEvent('airx:ai-portrait:ready', {
                detail: {
                  dataURL: dataURL,
                  styleId: 'curator',
                  styleTitle: 'Curator original',
                  source: 'curator',
                  printSize: { w: link.w, h: link.h, depth: link.depth },
                  artworkId: link.id
                }
              })
            );

            if (window.history && window.history.replaceState) {
              try {
                window.history.replaceState(null, '', urlWithoutCuratorParams());
              } catch (err) {

              }
            }
          });
        })
        .catch(function () {
          self.curatorNotice(expired, true);
        });
      return true;
    }

    curatorNotice(text, scroll) {
      var note = document.querySelector('[data-aips-curator-note]');
      if (!text) {
        if (note && note.parentNode) note.parentNode.removeChild(note);
        return;
      }
      if (!note) {
        note = document.createElement('div');
        note.className = 'cyo-note';
        note.setAttribute('data-aips-curator-note', '');
        note.setAttribute('role', 'status');
        note.style.maxWidth = '640px';
        note.style.marginInline = 'auto';
        var cyoRoot = document.querySelector('[data-cyo-root]');
        var host = cyoRoot ? (cyoRoot.querySelector('.cyo-hero') || cyoRoot) : null;
        if (host) host.appendChild(note);
        else this.insertBefore(note, this.firstChild);
      }
      note.textContent = text;
      if (scroll && note.scrollIntoView) {
        note.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
      }
    }

    renderGallery() {
      if (!this.galleryEl || !this.galleryListEl) return;
      var list = this.sessionArtworks;

      if (list.length < 2) {
        this.galleryEl.hidden = true;
        this.galleryListEl.textContent = '';
        return;
      }
      this.galleryEl.hidden = false;
      this.galleryListEl.textContent = '';
      for (var i = 0; i < list.length; i++) {
        var item = list[i];
        var li = document.createElement('li');
        li.className = 'aips-gallery__item';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'aips-gallery__tile' + (i === this.stagedArtwork ? ' is-staged' : '');
        btn.setAttribute('data-aips-gallery-pick', String(i));
        btn.setAttribute('aria-pressed', i === this.stagedArtwork ? 'true' : 'false');
        var caption = this.artworkCaption(item);

        var n = String(i + 1);
        var label = caption
          ? interp(this.strings.galleryTileLabel, { n: n, title: caption })
          : interp(this.strings.galleryTileLabelPlain, { n: n });
        btn.setAttribute('aria-label', label || caption || n);
        var img = document.createElement('img');
        img.className = 'aips-gallery__img';
        img.src = item.after;
        img.alt = '';
        img.loading = 'lazy';
        btn.appendChild(img);
        if (caption) {
          var cap = document.createElement('span');
          cap.className = 'aips-gallery__caption';
          cap.textContent = caption;
          btn.appendChild(cap);
        }
        li.appendChild(btn);
        this.galleryListEl.appendChild(li);
      }
    }

    artworkCaption(item) {
      if (!item) return '';
      if (item.title) return item.title;
      return item.prompt ? excerpt(item.prompt, 40) : '';
    }

    applyRevealLabel(mode, prompt) {
      if (!this.canvas3dHost) return;
      if (this._revealAltDefault == null) this._revealAltDefault = this.canvas3dHost.getAttribute('aria-label') || '';
      var label = this._revealAltDefault;
      if (mode === 'describe' && prompt && this.strings.revealAltDescribe) {
        label = interp(this.strings.revealAltDescribe, { prompt: excerpt(prompt, 120) });
      }
      this.canvas3dHost.setAttribute('aria-label', label);
      if (this.flatImg) this.flatImg.alt = label;
    }

    applyDownloadLink(image, style) {
      if (!this.downloadLink) return;
      this.downloadLink.href = image;
      var slug = String((style && style.id) || 'style').replace(/[^\w-]+/g, '-');
      if (this.jobMode === 'describe') {

        var described = String(this.strings.downloadNameDescribe || 'described').replace(/[^\w-]+/g, '-');
        slug = style && !style.auto && style.id ? described + '-' + slug : described;
      }
      var base = (this.strings.downloadName || 'airx-ai-artwork') + '-' + slug;

      var mimeMatch = /^data:image\/(\w+)/.exec(image);
      var ext = mimeMatch ? mimeMatch[1].toLowerCase() : 'png';
      if (ext === 'jpeg') ext = 'jpg';
      this.downloadLink.setAttribute('download', base + '.' + ext);
    }

    stageArtwork(index) {

      if (this.busy) return;
      var item = this.sessionArtworks[index];
      if (!item) return;
      this.stagedArtwork = index;
      this.jobStyle = item.style;
      this.jobImageDataURL = item.before;

      this.jobMode = item.mode || 'photo';
      this.jobPrompt = item.prompt || '';
      this.jobAspect = item.aspect || '';
      this.applyRevealLabel(this.jobMode, this.jobPrompt);
      this.stageEditsForNewArtwork(item.after);
      this.applyDownloadLink(this.resultDataURL, item.style);
      this.mountResultViewer(this.resultDataURL);
      this.renderGallery();
      this.liveStageArtwork(item);
      if (this.strings.galleryStaged) {
        this.announce(interp(this.strings.galleryStaged, { title: this.artworkCaption(item) }));
      }
    }

    ensureViewerScript() {
      if (this._viewerScriptPromise) return this._viewerScriptPromise;
      var src = this.dataset.viewerSrc || '';
      this._viewerScriptPromise = new Promise(function (resolve) {
        if (!src || window.customElements && customElements.get('canvas-viewer-3d')) return resolve(true);
        var tag = document.createElement('script');
        tag.src = src;
        tag.onload = function () { resolve(true); };
        tag.onerror = function () { resolve(false); };
        document.head.appendChild(tag);
      });
      return this._viewerScriptPromise;
    }

    mountResultViewer(dataURL) {
      var self = this;
      if (!this.canvas3dHost) return;
      var seq = (this._viewerSeq = (this._viewerSeq || 0) + 1);
      var showFlat = function () {
        if (seq !== self._viewerSeq) return;
        self.canvas3dHost.textContent = '';
        var img = document.createElement('img');
        img.className = 'aips-canvas3d__flat';
        img.src = dataURL;

        img.alt = self.canvas3dHost.getAttribute('aria-label') || '';
        self.canvas3dHost.appendChild(img);
      };
      this.ensureViewerScript().then(function (ok) {
        if (seq !== self._viewerSeq) return;
        if (!ok || !customElements.get('canvas-viewer-3d')) return showFlat();
        var probe = new Image();
        probe.onload = function () {
          if (seq !== self._viewerSeq) return;
          var r = probe.naturalWidth / Math.max(1, probe.naturalHeight);
          var size = r > 1.15 ? '36 x 24' : r < 0.87 ? '24 x 36' : '30 x 30';
          self.canvas3dHost.textContent = '';
          var viewer = document.createElement('canvas-viewer-3d');
          viewer.className = 'aips-canvas3d__viewer';
          viewer.dataset.threeSrc = self.dataset.threeSrc || '';
          viewer.dataset.texture = dataURL;
          viewer.dataset.initialSize = size;
          viewer.dataset.initialDepth = '1 1/2 Inch';
          viewer.addEventListener('canvas-viewer:unsupported', showFlat, { once: true });
          self.canvas3dHost.appendChild(viewer);
          self._viewer3d = viewer;

          if (viewer.activate) viewer.activate();
        };
        probe.onerror = showFlat;
        probe.src = dataURL;
      });
    }

    applyPersonalText() {

      this.refreshEdits();
    }

    cssFilter() {
      return 'brightness(' + this.edit.bright + '%) contrast(' + this.edit.contrast + '%) saturate(' + this.edit.sat + '%)';
    }

    editsAreNeutral() {
      var e = this.edit;
      var text = (this.personalInput ? this.personalInput.value : '').trim();
      return !text && e.bright === 100 && e.contrast === 100 && e.sat === 100;
    }

    composeArtwork(opts) {
      var self = this;
      var withText = !opts || opts.withText !== false;
      var withFilters = !opts || opts.withFilters !== false;
      return new Promise(function (resolve) {
        if (!self.resultBase) return resolve(null);
        var text = (self.personalInput ? self.personalInput.value : '').trim().slice(0, 80);
        var neutralFilter = self.edit.bright === 100 && self.edit.contrast === 100 && self.edit.sat === 100;
        if ((!withText || !text) && (!withFilters || neutralFilter)) return resolve(self.resultBase);
        var img = new Image();
        img.onload = function () {
          try {
            var w = img.naturalWidth, h = img.naturalHeight;
            var c = document.createElement('canvas');
            c.width = w; c.height = h;
            var ctx = c.getContext('2d');
            if (withFilters && !neutralFilter) ctx.filter = self.cssFilter();
            ctx.drawImage(img, 0, 0);
            ctx.filter = 'none';
            var finish = function () { resolve(c.toDataURL('image/png')); };
            if (withText && text) {
              var px = Math.min(w, h) * 0.055 * (self.edit.sizePct / 100);
              var font = px + "px '" + self.edit.font + "', cursive";
              var draw = function () {
                ctx.font = font;
                ctx.fillStyle = self.edit.color;
                ctx.shadowColor = 'rgba(0,0,0,0.45)';
                ctx.shadowBlur = px * 0.18;
                ctx.shadowOffsetY = px * 0.06;
                var pad = Math.min(w, h) * 0.05;
                var pos = self.edit.pos;
                ctx.textAlign = pos.indexOf('Left') > -1 ? 'left' : pos.indexOf('Right') > -1 ? 'right' : 'center';
                var x = pos.indexOf('Left') > -1 ? pad : pos.indexOf('Right') > -1 ? w - pad : w / 2;
                var y;
                if (pos.indexOf('Top') === 0) { ctx.textBaseline = 'top'; y = pad; }
                else { ctx.textBaseline = 'alphabetic'; y = h - pad; }
                ctx.fillText(text, x, y);
                finish();
              };
              try { document.fonts.load(font, text).then(draw, draw); } catch (e) { draw(); }
            } else finish();
          } catch (e) { resolve(self.resultBase); }
        };
        img.onerror = function () { resolve(self.resultBase); };
        img.src = self.resultBase;
      });
    }

    refreshEdits(immediate) {
      var self = this;
      window.clearTimeout(this._editTimer);
      var delay = immediate ? 0 : (this.resultView === '3d' ? 650 : 300);
      this._editTimer = window.setTimeout(function () {
        var seq = (self._editSeq = (self._editSeq || 0) + 1);
        self.composeArtwork({ withText: true, withFilters: false }).then(function (flatUrl) {
          if (seq !== self._editSeq || !flatUrl) return;
          if (self.flatImg) {
            self.flatImg.src = flatUrl;
            self.flatImg.style.filter = self.cssFilter();
          }
        });
        self.composeArtwork({ withText: true, withFilters: true }).then(function (fullUrl) {
          if (seq !== self._editSeq || !fullUrl) return;
          self.resultDataURL = fullUrl;
          self.applyDownloadLink(fullUrl, self.jobStyle || self.selectedStyle);
          if (self.resultView === '3d') self.mountResultViewer(fullUrl);
        });
      }, delay);
    }

    stageEditsForNewArtwork(image) {
      this.resultBase = image;
      this.resultDataURL = image;
      if (this.flatImg) { this.flatImg.src = image; this.flatImg.style.filter = this.cssFilter(); }
      this.refreshEdits(true);
    }

    setResultView(view) {
      this.resultView = view === 'art' ? 'art' : '3d';
      var art = this.resultView === 'art';
      if (this.flatWrap) this.flatWrap.hidden = !art;
      if (this.canvas3dHost) this.canvas3dHost.hidden = art;
      this.viewBtns.forEach(function (b) {
        var on = (b.getAttribute('data-aips-view') === (art ? 'art' : '3d'));
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (!art) this.refreshEdits(true);
      else this.hideLoupe();
    }

    bindLoupe() {
      var self = this;
      if (!this.flatWrap || !this.loupeEl) return;
      if (!window.matchMedia('(pointer: fine)').matches) return;
      var ZOOM = 2, SIZE = 180;
      this.flatWrap.addEventListener('pointermove', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        if (!self.flatImg || !self.flatImg.src || self.flatWrap.hidden) return;
        var r = self.flatImg.getBoundingClientRect();
        var x = e.clientX - r.left, y = e.clientY - r.top;
        if (x < 0 || y < 0 || x > r.width || y > r.height) { self.hideLoupe(); return; }
        var l = self.loupeEl;
        l.hidden = false;
        l.style.width = SIZE + 'px';
        l.style.height = SIZE + 'px';
        l.style.left = (e.clientX - self.flatWrap.getBoundingClientRect().left - SIZE / 2) + 'px';
        l.style.top = (e.clientY - self.flatWrap.getBoundingClientRect().top - SIZE / 2) + 'px';
        l.style.backgroundImage = 'url("' + self.flatImg.src + '")';
        l.style.backgroundSize = (r.width * ZOOM) + 'px ' + (r.height * ZOOM) + 'px';
        l.style.backgroundPosition = (-(x * ZOOM - SIZE / 2)) + 'px ' + (-(y * ZOOM - SIZE / 2)) + 'px';
        l.style.filter = self.cssFilter();
      });
      this.flatWrap.addEventListener('pointerleave', function () { self.hideLoupe(); });
    }
    hideLoupe() { if (this.loupeEl) this.loupeEl.hidden = true; }

    printOnCanvas() {
      if (!this.resultBase) return;
      var self = this;

      var style = this.jobStyle || this.selectedStyle;
      this.composeArtwork({ withText: false, withFilters: true }).then(function (printUrl) {
        self.dispatchPrintReady(printUrl || self.resultBase, style);
      });
    }

    dispatchPrintReady(dataURL, style) {
      var described = this.jobMode === 'describe';
      var auto = !!(style && style.auto);

      var styleId = auto ? (described ? 'described' : '') : ((style && style.id) || '');
      var styleTitle = auto ? '' : ((style && (style.displayTitle || style.title)) || '');
      document.dispatchEvent(
        new CustomEvent('airx:ai-portrait:ready', {
          detail: {
            dataURL: dataURL,
            styleId: styleId,
            styleTitle: styleTitle,
            source: described ? 'describe' : 'photo',
            customText: this.personalInput ? this.personalInput.value.trim().slice(0, 80) : '',
            textPosition: this.personalPos ? this.personalPos.value : ''
          }
        })
      );

    }

    tryAnotherStyle() {
      var self = this;

      this.liveHide();

      this.loadQuota().then(function (q) {
        if (self.isOutOfQuota(q)) {
          self.showQuotaCard(q);
          return;
        }
        self.hideStage();
        self.resultDataURL = null;
        if (self.railEl) {
          var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          self.railEl.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });

          var selected = self.railEl.querySelector('.aips-tile.is-selected:not([hidden])') || self.railEl.querySelector('.aips-tile:not([hidden])');
          if (selected) selected.focus({ preventScroll: true });
        }
        self.updateReadiness();
      });
    }
  }

  customElements.define('ai-portrait-studio', AIPortraitStudio);
})();

(function () {
  'use strict';

  var MONTH_LABEL = null;
  var YEAR_LABEL = null;

  function labels() {
    if (MONTH_LABEL !== null) return;
    var el = document.querySelector('[data-aips-plans]');
    MONTH_LABEL = (el && el.dataset.labelMonth) || 'per month';
    YEAR_LABEL = (el && el.dataset.labelYear) || 'per year, billed once';
  }

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-aips-term]');
    if (!btn) return;
    var wrap = btn.closest('[data-aips-plans]');
    if (!wrap) return;
    labels();

    var term = btn.dataset.aipsTerm === 'annual' ? 'annual' : 'monthly';

    wrap.querySelectorAll('[data-aips-term]').forEach(function (b) {
      var on = b === btn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });

    wrap.querySelectorAll('[data-aips-price]').forEach(function (p) {
      var next = p.dataset[term];
      if (next) p.textContent = next;
    });
    wrap.querySelectorAll('[data-aips-term-label]').forEach(function (l) {
      l.textContent = term === 'annual' ? YEAR_LABEL : MONTH_LABEL;
    });
    wrap.querySelectorAll('[data-aips-plan-cta]').forEach(function (a) {
      var href = a.dataset[term];
      if (href) a.setAttribute('href', href);
    });
  });
})();
